import React, { useState, useEffect, useCallback } from 'react';

const TTD_PORTAL_BASE = 'https://www.kromarketplace.com/ttd-portal';

const KRO_API_BASE = process.env.REACT_APP_KRO_API_BASE
  || 'https://ca-api-prod-wus2.reddesert-84090673.westus2.azurecontainerapps.io/api/v1';

// MainNet-only: DevNet/TestNet are strictly isolated and never surfaced here.
const CANTON_ENV = 'mainnet';

const efcpStyle = document.createElement('style');
efcpStyle.textContent = `@keyframes efcp-spin{to{transform:rotate(360deg)}}.efcp-spin{animation:efcp-spin 1s linear infinite;display:inline-block}@keyframes efcp-pulse{0%,100%{opacity:1}50%{opacity:.4}}.efcp-pulse{animation:efcp-pulse 2s ease-in-out infinite}`;
if (!document.getElementById('efcp-gw-style')) { efcpStyle.id = 'efcp-gw-style'; document.head.appendChild(efcpStyle); }

const EXECUTION_PROVIDERS = [
  { id: 'canton', name: 'Canton Network — Daml Ledger', icon: 'bi-hdd-network-fill', badge: 'CONFIRMED',
    detail: 'DrawWorkflow / SettlementWorkflow contract authorisation on Canton Mainnet validator KRO-Mainnet-1',
    refs: { contractId: 'Canton::KRO-Mainnet-1::DrawWorkflow#12a4', validatorNode: 'KRO-Mainnet-1' },
    verify: 'Query Canton Ledger API: grpcurl GetTransactions for DrawWorkflow / SettlementWorkflow on KRO-Mainnet-1' },
  { id: 'bitgo', name: 'BitGo Enterprise — MPC Custody', icon: 'bi-shield-lock-fill', badge: 'CONNECTED',
    detail: 'Enterprise wallet connectivity — HASH wallet (Provenance markers), ETH wallet (USDT escrow), MPC 2-of-3 quorum active',
    refs: { apiKey: 'bg-ent-****-****-7f3a', hashWallet: 'pb1mktt...5mp6', ethWallet: '0x6855...5ba6' },
    verify: 'BitGo API: GET /api/v2/thash/wallet/{walletId}/transfer · ETH wallet on Etherscan: 0x6855...5ba6' },
  { id: 'provenance', name: 'Provenance Blockchain', icon: 'bi-link-45deg', badge: 'CONFIRMED',
    detail: 'kro.as.accountability.v2 marker delivery on pio-mainnet-1 — DTIF linkage verified, ZoneScan verifiable',
    refs: { denom: 'kro.as.accountability.v2', chain: 'pio-mainnet-1', dtif: 'ZX18049M0' },
    verifyUrl: 'https://zonescan.io/provenance/assets/kro.as.accountability.v2', verifyLabel: 'Verify on ZoneScan (182 units)' },
  { id: 'cip56-cash', name: 'CIP-56 Cash Leg (On-Ledger)', icon: 'bi-coin', badge: 'READY',
    detail: 'On-chain CIP-56 cash token — the DvP cash leg settles on the Canton Global Synchronizer, atomically with the collateral leg',
    refs: { standard: 'CIP-0056', dvp: 'Allocation_ExecuteTransfer', finality: 'T+0 deterministic' },
    verify: 'Canton Ledger API: GetTransactions for Allocation / TransferInstruction on KRO-Mainnet-1 — both legs settle in one indivisible transaction' },
  { id: 'fedwire', name: 'Mercury → Column N.A. → FedWire RTGS', icon: 'bi-building-fill', badge: 'CONFIRMED',
    detail: 'Optional off-ledger USD rail — Column N.A. routing 121145433, SWIFT CLUMUS33XXX, ~2h RTGS settlement window',
    refs: { routing: '121145433', swift: 'CLUMUS33XXX', settlementWindow: '~2h RTGS' },
    verify: 'Mercury API: GET /api/v1/transactions · Fed reference assigned by Federal Reserve' },
];

const BLANK_AFFIDAVIT = {
  nonce: 1,
  lei: '',
  organizationVleiRoot: '',
  signatoryOor: '',
  signatoryRole: '',
  executingEntity: '',
  executingWallet: '',
  fundingSource: '',
  settlementType: '',
  priority: '',
  destination: '',
  attestation: 'Exercising Executive Signing Authority as SSP to facilitate a Sovereign Treasury Draw with Discretionary Social Purpose Allocation. Word is Bond.',
  signatureAttachment: null,
};

const ExecutionGateway = () => {
  const [activeView, setActiveView] = useState('pipeline');
  const [feedStatus, setFeedStatus] = useState({ connected: false, lastSync: null, events: [] });
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [cantonTab, setCantonTab] = useState('pcp'); // 'pcp' | 'repo' | 'ledger'
  const [cantonLedger, setCantonLedger] = useState({ summary: null, contracts: [], loading: false, loaded: false });

  // Affidavit data feed state
  const [affidavit, setAffidavit] = useState(BLANK_AFFIDAVIT);
  const [affidavitRecordId, setAffidavitRecordId] = useState(null);
  const [affidavitLastSync, setAffidavitLastSync] = useState(null);
  const [affidavitSaving, setAffidavitSaving] = useState(false);
  const [affidavitStatus, setAffidavitStatus] = useState('');
  const [allRecords, setAllRecords] = useState([]);
  // Track last HTTP status from attestation API so UI can show a banner on 500
  const [attestationApiStatus, setAttestationApiStatus] = useState(null); // null | 200 | 500 | etc.

  const fetchAffidavit = useCallback(async () => {
    try {
      const res = await fetch(`${KRO_API_BASE}/attestation/latest`);
      setAttestationApiStatus(res.status);
      if (!res.ok) {
        console.warn(`[ExecutionGateway] attestation/latest → HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      if (data.success && data.attestation) {
        const rec = data.attestation;
        setAffidavitRecordId(rec.id);
        setAffidavitLastSync(rec.updatedAt);
        setAffidavit({
          lei:                  rec.lei ?? '',
          organizationVleiRoot: rec.organizationVleiRoot ?? '',
          signatoryOor:         rec.signatoryOor ?? '',
          signatoryRole:        rec.signatoryRole ?? '',
          nonce:                rec.nonce ?? 1,
          executingEntity:      rec.executingEntity ?? '',
          executingWallet:      rec.executingWallet ?? '',
          fundingSource:        rec.fundingSource ?? '',
          settlementType:       rec.settlementType ?? '',
          priority:             rec.priority ?? '',
          destination:          rec.destination ?? '',
          attestation:          rec.attestation ?? '',
        });
      }
    } catch (e) {
      setAttestationApiStatus(0);
      console.warn('[ExecutionGateway] affidavit fetch error:', e.message);
    }
  }, []);

  const fetchAllRecords = useCallback(async () => {
    try {
      const res = await fetch(`${KRO_API_BASE}/attestation/all`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) setAllRecords(data.attestations || []);
    } catch (e) {
      console.warn('[ExecutionGateway] all records fetch error:', e.message);
    }
  }, []);

  const pushAffidavitToKRO = async () => {
    setAffidavitSaving(true);
    setAffidavitStatus('');
    try {
      const res = await fetch(`${KRO_API_BASE}/attestation/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attestation: { ...affidavit, id: affidavitRecordId, status: 'ACTIVE' },
          source: 'TTD_STUDIO',
        }),
      });
      if (res.status === 500) {
        setAffidavitStatus('Backend API unavailable (HTTP 500). Check Azure Container App logs and retry when service is restored.');
        return;
      }
      if (res.status === 405 || res.status === 404) {
        setAffidavitStatus('Attestation API endpoint is not yet deployed. Contact backend team.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        setAffidavitRecordId(data.id);
        setAffidavitLastSync(data.updatedAt);
        setAffidavitStatus('Pushed to KRO Marketplace successfully.');
        fetchAllRecords();
      } else {
        setAffidavitStatus(`Error: ${data.error || res.statusText}`);
      }
    } catch (e) {
      setAffidavitStatus(`Network error: ${e.message}`);
    } finally {
      setAffidavitSaving(false);
      setTimeout(() => setAffidavitStatus(''), 6000);
    }
  };

  const handleSignatureAttachment = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { alert('Signature image must be under 1 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setAffidavit(prev => ({
      ...prev,
      signatureAttachment: { name: file.name, dataUrl: ev.target.result },
    }));
    reader.readAsDataURL(file);
  };

  const syncFeedStatus = useCallback(async () => {
    try {
      // Fetch live Canton events from backend (all envs, most recent 20)
      // Public on-ledger read endpoints — no privileged credential is sent from
      // the browser (the SYSTEM_ADMIN key was removed from the bundle).
      const [evtRes, sumRes] = await Promise.all([
        fetch(`${KRO_API_BASE}/canton/contracts?env=${CANTON_ENV}&limit=20`),
        fetch(`${KRO_API_BASE}/canton/summary`),
      ]);

      const evtData = evtRes.ok ? await evtRes.json() : {};
      const sumData = sumRes.ok ? await sumRes.json() : {};
      const records = evtData.records || [];

      // Map canton_event_log rows → feed event format
      // Event-type keys MUST match the canonical event_type strings the backend
      // actually writes to canton_event_log (cantonSettlementBridge.js +
      // cantonContractsRoutes ACTIVE_TYPES). The intraday-repo lifecycle is
      // emitted as INTRADAY_* — the older REPO_FACILITY_ACTIVE / REPO_DRAW_*
      // keys were never produced by the backend, so rows fell through to the raw
      // event_type fallback. Only keys the backend emits are mapped here.
      const EVENT_TYPE_MAP = {
        // PCP (pledge → draw → settle) lifecycle
        COLLATERAL_ACTIVE:        { type: 'MARKER_ASSIGN', label: 'Collateral Active',     provider: 'Canton' },
        PLEDGE_CREATED:           { type: 'CANTON_PLEDGE', label: 'Pledge Created',        provider: 'Canton' },
        DRAW_ACCEPTED:            { type: 'CANTON_DRAW',   label: 'Draw Accepted',         provider: 'Canton' },
        SETTLEMENT_CONFIRMED:     { type: 'CANTON_SETTLE', label: 'Settlement Confirmed',  provider: 'Canton' },
        CLOSEOUT_ACTIVE:          { type: 'CLOSEOUT',      label: 'CloseOut Receipt',      provider: 'Canton' },
        // Intraday repo lifecycle (canonical INTRADAY_* — cantonSettlementBridge)
        INTRADAY_RFP:             { type: 'REPO_RFP',      label: 'Intraday Repo RFQ',     provider: 'Canton' },
        INTRADAY_FACILITY:        { type: 'REPO_FACILITY', label: 'Intraday Facility',     provider: 'Canton' },
        INTRADAY_FACILITY_CREATED:{ type: 'REPO_FACILITY', label: 'Intraday Facility',     provider: 'Canton' },
        INTRADAY_DRAW:            { type: 'REPO_DRAW',     label: 'Intraday Repo Draw',    provider: 'Canton' },
        INTRADAY_CLOSEOUT:        { type: 'CLOSEOUT',      label: 'Intraday CloseOut',     provider: 'Canton' },
        REPO_ROLLOVER:            { type: 'REPO_ROLLOVER', label: 'Automatic Rollover',    provider: 'Canton' },
        REPO_ROLLOVER_ACTIVE:     { type: 'REPO_ROLLOVER', label: 'Rollover Record',       provider: 'Canton' },
      };

      const STATUS_MAP = {
        COLLATERAL_ACTIVE:        'ACTIVE',
        PLEDGE_CREATED:           'CREATED',
        DRAW_ACCEPTED:            'ACCEPTED',
        SETTLEMENT_CONFIRMED:     'CONFIRMED',
        CLOSEOUT_ACTIVE:          'SETTLED',
        INTRADAY_RFP:             'OPEN',
        INTRADAY_FACILITY:        'ACTIVE',
        INTRADAY_FACILITY_CREATED:'ACTIVE',
        INTRADAY_DRAW:            'OPEN',
        INTRADAY_CLOSEOUT:        'SETTLED',
        REPO_ROLLOVER:            'ROLLED',
        REPO_ROLLOVER_ACTIVE:     'ROLLED',
      };

      const mappedEvents = records.map((r, i) => {
        const meta    = typeof r.metadata === 'string' ? (() => { try { return JSON.parse(r.metadata); } catch { return {}; } })() : (r.metadata || {});
        const typeMap = EVENT_TYPE_MAP[r.event_type] || { type: r.event_type, label: r.event_type, provider: 'Canton' };
        const status  = STATUS_MAP[r.event_type] || 'RECEIVED';
        const detail  = r.uetr
          ? `UETR: ${r.uetr}${r.amount_usd ? `  ·  $${(r.amount_usd / 1e6).toFixed(2)}M` : ''}`
          : meta.denom
            ? `${meta.denom}${meta.units ? `  ·  ${meta.units} units` : ''}`
            : meta.drawId || meta.facilityId || r.event_type;
        return {
          id:       r.id || i + 1,
          type:     typeMap.type,
          label:    typeMap.label,
          denom:    detail,
          workflow: typeMap.label,
          ref:      r.uetr || meta.drawId || '',
          status,
          provider: typeMap.provider,
          env:      meta.env || r.env || CANTON_ENV,
          ts:       r.event_timestamp || r.created_at,
          amountUsd: r.amount_usd,
        };
      });

      setFeedStatus({
        connected: true,
        lastSync:  new Date().toISOString(),
        events:    mappedEvents,
        summary:   sumData.summary || {},
      });

    } catch (err) {
      setFeedStatus(prev => ({ ...prev, connected: false, lastSync: new Date().toISOString() }));
    }
  }, []);

  useEffect(() => {
    syncFeedStatus();
    fetchAffidavit();
    fetchAllRecords();

    // Fetch Canton network tier on mount — drives all status labels automatically.
    // NOTE: this block previously sat AFTER the cleanup `return` below, which made it
    // unreachable dead code, so `cantonNetworkStatus` never updated from this call.
    // Moved ahead of the return so it actually runs on mount.
    fetch(`${KRO_API_BASE}/canton/summary`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.summary) {
          const s = data.summary;
          setCantonNetworkStatus({
            loading: false,
            network:        'MainNet',
            mainnetRecords: s.mainnetRecords  || 0,
            testnetRecords: 0,
            devnetRecords:  0,
            packageVersion: s.packageVersion  || '0.1.3',
            partyId:        s.partyId         || '',
            lastEventAt:    s.lastEventAt     || null,
          });
        } else {
          setCantonNetworkStatus(s => ({ ...s, loading: false, network: 'MainNet' }));
        }
      })
      .catch(() => setCantonNetworkStatus(s => ({ ...s, loading: false, network: 'MainNet' })));

    // Auto-refresh feed every 30 seconds
    const feedInterval = setInterval(syncFeedStatus, 30_000);
    return () => clearInterval(feedInterval);
  }, [syncFeedStatus, fetchAffidavit, fetchAllRecords]);


  // Canton network live status — fetched on mount, drives all status labels
  const [cantonNetworkStatus, setCantonNetworkStatus] = useState({
    loading: true, network: null,
    mainnetRecords: 0, testnetRecords: 0, devnetRecords: 0,
    packageVersion: null, partyId: null, lastEventAt: null,
  });

  // Sandbox E2E test state
  const [sandboxRunning, setSandboxRunning] = useState(false);
  const [sandboxStep, setSandboxStep] = useState(-1);
  const [sandboxLog, setSandboxLog] = useState([]);
  const [sandboxReceipt, setSandboxReceipt] = useState(null);

  const genHex = (len) => Array.from(crypto.getRandomValues(new Uint8Array(len))).map(b => b.toString(16).padStart(2,'0')).join('');
  const genUUID = () => crypto.randomUUID();

  const runSandboxTest = useCallback(async () => {
    setSandboxRunning(true);
    setSandboxStep(0);
    setSandboxLog([]);
    setSandboxReceipt(null);

    const testId = `SBX-${Date.now()}`;
    const registrySeq = Math.floor(100000 + Math.random() * 900000);
    const rootRegistryKey = 'RSID17738103:CHR2:DF98:17738103:DNAMARGUERITEOPHELIA';
    const uetr = genUUID();
    const cantonContractId = `Canton::KRO-Mainnet-1::DrawWorkflow#${genHex(4)}`;
    const provenanceTxHash = genHex(32).toUpperCase();
    const bitgoTxHash = genHex(32).toUpperCase();
    const fedRef = `FW${Date.now().toString().slice(-10)}`;
    const blockHeight = 18940000 + Math.floor(Math.random() * 10000);

    const addLog = (msg, type = 'info') => {
      const entry = { ts: new Date().toISOString(), msg, type };
      setSandboxLog(prev => [...prev, entry]);
      return entry;
    };

    const stepDelay = (ms) => new Promise(r => setTimeout(r, ms));

    const haircutPct = 2;
    const notional = 10000000;
    const collateralRequired = Math.round(notional / (1 - haircutPct / 100));
    const facilityLimit = 755000000;
    const nonce = Math.floor(Math.random() * 9999) + 1;

    const steps = [
      {
        label: 'Attestation of Affidavit — Authority of Record',
        run: async () => {
          addLog(`[AFFIDAVIT] Initiating Attestation of Authority — sandbox test ${testId}`);
          addLog(`[AFFIDAVIT] Signatory: Special Situations Partner (SSP) — KRO Encrypted Virtual ID`);
          addLog(`[AFFIDAVIT] LEI binding: ISO 17442 — GLEIF-registered Legal Entity Identifier (KRO holds LEI)`);
          addLog(`[AFFIDAVIT] Organization credential: Encrypted Virtual ID modeled on vLEI/KERI best practices`);
          await stepDelay(600);
          addLog(`[AFFIDAVIT] KRO Virtual ID verified — role binding confirmed`);
          addLog(`[AFFIDAVIT] Nonce: ${nonce} — replay protection`);
          addLog(`[AFFIDAVIT] Attestation body: "Exercising Executive Signing Authority as SSP to facilitate a Sovereign Treasury Draw with Discretionary Social Purpose Allocation."`);
          await stepDelay(400);
          addLog(`[AFFIDAVIT] SHA-256 credential digest generated — anchored to encrypted event log`, 'success');
          addLog(`[AFFIDAVIT] Witness network propagation: 3/3 witnesses acknowledged`);
          addLog(`[AFFIDAVIT] Authority of Record SEALED — execution pipeline authorized`);
        }
      },
      {
        label: 'Provenance — Collateral Posting + Adequacy Check',
        run: async () => {
          addLog(`[PROVENANCE] Connecting to pio-mainnet-1 via Figure Markets enterprise account...`);
          await stepDelay(500);
          addLog(`[PROVENANCE] Querying marker supply: kro.as.accountability.v2`);
          addLog(`[PROVENANCE] On-chain supply: 182 units · NAV/unit: $5,000,000 · Total marked: $910,000,000 (canonical — verified on ZoneScan)`);
          addLog(`[PROVENANCE] DTIF linkage: ZX18049M0 — Digital Token Identifier Foundation registry confirmed`);
          await stepDelay(400);
          addLog(`[PROVENANCE] Collateral and exposure report generated`);
          addLog(`[PROVENANCE] Collateral adequacy check: notional=$${(notional/1e6).toFixed(1)}M · haircut=${haircutPct}% · required collateral=$${(collateralRequired/1e6).toFixed(2)}M`);
          addLog(`[PROVENANCE] collateral*(1−haircut) ≥ notional: $${(collateralRequired*(1-haircutPct/100)/1e6).toFixed(2)}M ≥ $${(notional/1e6).toFixed(1)}M ✓`);
          addLog(`[PROVENANCE] Escrow assignment instruction: MARKER_ASSIGN → status AWAITING_PAYMENT`);
          addLog(`[PROVENANCE] Block height: ${blockHeight} · TX Hash: ${provenanceTxHash.slice(0,8)}...${provenanceTxHash.slice(-8)}`);
          await stepDelay(300);
          addLog(`[PROVENANCE] Webhook → Azure: MARKER_ASSIGN event received by ca-api-prod-wus2`, 'success');
          addLog(`[PROVENANCE] ZoneScan verifiable: https://zonescan.io/provenance/tx/${provenanceTxHash.slice(0,8)}...`);
        }
      },
      {
        label: 'IntradayRepoRFP — Counterparty Accepts KRO RFQ for Review',
        run: async () => {
          addLog(`[RFQ] Broadcasting Repo.Intraday:IntradayRepoRFP (price-discovery RFQ) to counterparty on Canton MainNet...`);
          await stepDelay(400);
          addLog(`[RFQ] ┌─ IntradayRepoFacility: ACTIVE · limit $${(facilityLimit/1e6).toFixed(0)}M · usedCapacity: $0`);
          addLog(`[RFQ] │  requestor (signatory): KRO-Mainnet-1 (RFQ initiator · cash buyer · accountability credits)`);
          addLog(`[RFQ] │  approvedRespondent:    Drawing Institution (eligible respondent · AcceptRFP controller)`);
          addLog(`[RFQ] │  custodian:             BitGo MPC (tri-party custody)`);
          addLog(`[RFQ] │  kroOS:                 OBSERVER ONLY — no execution authority`);
          await stepDelay(400);
          addLog(`[RFQ] ├─ KRO presents standard RFQ terms — request for indicative quotes delivered for counterparty review (M2M):`);
          addLog(`[RFQ] │  uetr:              ${uetr}`);
          addLog(`[RFQ] │  rfqAmount:         $${(notional/1e6).toFixed(1)}M USD per draw`);
          addLog(`[RFQ] │  collateralRef:     kro.as.accountability.v2`);
          addLog(`[RFQ] │  pricingInquiry:    intraday T+0 rate — respondents submit indicative quotes (ACT/360)`);
          addLog(`[RFQ] │  startTime:         ${new Date().toISOString()}`);
          addLog(`[RFQ] │  expiryTime:        ${new Date(Date.now() + 3600000).toISOString()} (RFQ window — auto-expires)`);
          addLog(`[RFQ] │  pilot constraints: $10M cap/txn · 2 txn/day · 10-day window · accountability credits only`);
          await stepDelay(400);
          addLog(`[RFQ] ├─ Counterparty (respondent) prompted to AcceptRFP — review & quote...`);
          await stepDelay(300);
          addLog(`[RFQ] │  Instrument left open for counterparty's standard classification — no KRO-imposed instrument`);
          addLog(`[RFQ] │  Counterparty supplies on accept: custodian · clearingAgent · capacityLimit · collateralValue · maturityTime`);
          addLog(`[RFQ] │  Collateral adequacy enforced by Daml: collateralValue × (1 − ${haircutPct}% haircut) ≥ $${(notional/1e6).toFixed(1)}M ✓`);
          addLog(`[RFQ] │  DTIF registry: ZX18049M0 — identifier confirmed on Global Synchronizer`);
          addLog(`[RFQ] │  Sub-transaction privacy: terms visible only to direct parties`);
          await stepDelay(300);
          addLog(`[RFQ] ├─ Counterparty accepts KRO RFQ at best quoted price — agreement established`, 'success');
          addLog(`[RFQ] │  IntradayRepoFacility + IntradayRepoDraw CREATED atomically via AcceptRFP`);
          addLog(`[RFQ] │  HeritageNetworkRecord anchored — requestor + respondent + kroOS`);
          addLog(`[RFQ] │  DrawWorkflow contract ID: ${cantonContractId}`);
          addLog(`[RFQ] │  openDraw state: AWAITING_ALLOCATION`);
          addLog(`[RFQ] └─ RFQ flow complete — counterparty-accepted draw authorized, advancing to CIP-0056 allocation lock`);
        }
      },
      {
        label: 'CIP-0056 Allocation Lock — Net Amount Reserved',
        run: async () => {
          addLog(`[CIP-0056] Initiating allocation lock per Canton Foundation Token Standard...`);
          await stepDelay(400);
          addLog(`[CIP-0056] AllocationFactory_Allocate exercised on collateral registry`);
          addLog(`[CIP-0056] Collateral Allocation: kro.as.accountability.v2 · 1 unit ($5M) locked`);
          addLog(`[CIP-0056] Cash Allocation: $${(notional/1e6).toFixed(1)}M USD reserved on cash registry`);
          addLog(`[CIP-0056] Net locking per CIP-0112: only delta reserved — capital drag minimized`);
          await stepDelay(300);
          addLog(`[CIP-0056] allocateBefore deadline: ${new Date(Date.now() + 3600000).toISOString()} (1h window)`);
          addLog(`[CIP-0056] Executor party: settlement agent (nonconsuming lifecycle control)`);
          addLog(`[CIP-0056] Both legs allocated — settlement can proceed`, 'success');
          addLog(`[CIP-0056] Sub-transaction privacy: allocation visible only to counterparties`);
        }
      },
      {
        label: 'BitGo Enterprise — MPC Custody Gate',
        run: async () => {
          addLog(`[BITGO] Connecting to BitGo Enterprise API (SOC 2 Type II audited)...`);
          await stepDelay(400);
          addLog(`[BITGO] Enterprise account: KRO Brokers SPC`);
          addLog(`[BITGO] HASH wallet (Provenance markers): pb1mktt...5mp6 — balance queried on pio-mainnet-1`);
          addLog(`[BITGO] ETH wallet (USDT escrow):        0x6855...5ba6 — ERC-20 balance confirmed on Ethereum mainnet`);
          await stepDelay(400);
          addLog(`[BITGO] Tri-party custody verification: NAV revalued at real-time oracle price`);
          addLog(`[BITGO] Haircut ${haircutPct}% re-applied on verified NAV · Concentration limit: single-issuer ≤ 25% — PASSED`);
          addLog(`[BITGO] MPC key ceremony — 2-of-3 threshold signature scheme:`);
          addLog(`[BITGO]   Share 1 (BitGo HSM):     APPROVED — hardware security module signature`);
          await stepDelay(300);
          addLog(`[BITGO]   Share 2 (Client co-sign): APPROVED — enterprise policy engine authorized`);
          addLog(`[BITGO]   Share 3 (Backup):         NOT NEEDED — threshold already met`);
          addLog(`[BITGO] MPC quorum: 2/3 — custody gate PASSED`, 'success');
          addLog(`[BITGO] TX Hash: ${bitgoTxHash.slice(0,8)}...${bitgoTxHash.slice(-8)}`);
          addLog(`[BITGO] Audit trail: transfer ID logged in BitGo Enterprise audit log`);
        }
      },
      {
        label: 'Clearing & Compliance Gate — KYC/AML/Sanctions',
        run: async () => {
          addLog(`[CLEARING] Initiating pre-settlement compliance gate...`);
          await stepDelay(400);
          addLog(`[CLEARING] KYC/AML attestation: GLEIF LEI + KRO Encrypted Virtual ID — VALID`);
          addLog(`[CLEARING] Sanctions screening: OFAC SDN list — CLEAR`);
          addLog(`[CLEARING] Sanctions screening: EU consolidated list — CLEAR`);
          addLog(`[CLEARING] Sanctions screening: UN Security Council — CLEAR`);
          await stepDelay(300);
          addLog(`[CLEARING] Nonconsuming Daml choice — draw contract remains active for DvP`);
          addLog(`[CLEARING] Clearing Gate: PASSED — all compliance checks binary PASS`, 'success');
          addLog(`[CLEARING] KRO role: settlement infrastructure and admin governance — observer only on settlement contracts`);
        }
      },
      {
        label: 'Global Synchronizer — Cross-Sync Reassignment',
        run: async () => {
          addLog(`[GLOBAL_SYNC] Initiating cross-synchronizer reassignment per Canton Foundation open-source DvP pattern...`);
          await stepDelay(400);
          addLog(`[GLOBAL_SYNC] Phase 1: Unassign collateral contract from KRO-Mainnet-1 (home sync)`);
          addLog(`[GLOBAL_SYNC] Phase 2: Assign collateral contract to Global Synchronizer (settlement sync)`);
          await stepDelay(300);
          addLog(`[GLOBAL_SYNC] Phase 1: Unassign cash contract from payment sync`);
          addLog(`[GLOBAL_SYNC] Phase 2: Assign cash contract to Global Synchronizer`);
          await stepDelay(300);
          addLog(`[GLOBAL_SYNC] Both legs now on same synchronizer — atomic DvP can proceed`);
          addLog(`[GLOBAL_SYNC] BFT consensus: Global Synchronizer ordering encrypted views`);
          addLog(`[GLOBAL_SYNC] Sub-transaction privacy: synchronizer never decrypts payloads`, 'success');
          addLog(`[GLOBAL_SYNC] Reassignment carries no settlement risk — value moves only at atomic step`);
        }
      },
      {
        label: 'Canton Atomic DvP — Allocation_ExecuteTransfer',
        run: async () => {
          addLog(`[CANTON] Connecting to participant node KRO-Mainnet-1 (operated by Infra Singularity)...`);
          await stepDelay(400);
          addLog(`[CANTON] ┌─ IntradayRepoFacility: ACTIVE — limit $${(facilityLimit/1e6).toFixed(0)}M · usedCapacity: $0`);
          addLog(`[CANTON] │  Parties: collateralProvider (Regulated FI) · collateralReceiver (Drawing Institution) · custodian (BitGo MPC) · kroOS (observer ONLY)`);
          await stepDelay(400);
          addLog(`[CANTON] ├─ Executor exercises Allocation_ExecuteTransfer on all legs simultaneously`);
          addLog(`[CANTON] │  Collateral leg: marker transfer on Provenance ↔ Cash leg: on-chain CIP-56 token`);
          addLog(`[CANTON] │  Single indivisible Daml transaction — both legs settle or both roll back`);
          addLog(`[CANTON] │  usedCapacity += $${(notional/1e6).toFixed(1)}M → availableCapacity: $${((facilityLimit-notional)/1e6).toFixed(0)}M`);
          addLog(`[CANTON] │  Contract ID: ${cantonContractId}`);
          await stepDelay(400);
          addLog(`[CANTON] │  Atomic DvP — both legs settle or both roll back · deterministic T+0 finality`);
          addLog(`[CANTON] │  Allocation contracts consumed — settlement FINAL`, 'success');
          await stepDelay(300);
          addLog(`[CANTON] ├─ Margin monitoring activated: 15-min revaluation cycle during open repo`);
          addLog(`[CANTON] │  MarginCall choice: auto-fires if exposure > maintenance threshold`);
          addLog(`[CANTON] │  Collateral substitution: SubstituteCollateral choice available (atomic swap, custodian re-verify)`);
          await stepDelay(300);
          addLog(`[CANTON] ├─ ReturnFunds: Collateral Receiver returns funds intraday`);
          addLog(`[CANTON] │  usedCapacity −= $${(notional/1e6).toFixed(1)}M → capacity FULLY regenerated (revolving)`);
          addLog(`[CANTON] │  Repo interest: notional × rate × (ACT/360) settled atomically at maturity`);
          await stepDelay(300);
          addLog(`[CANTON] └─ Post-DvP reassignment: assets returned to home synchronizers`);
          addLog(`[CANTON]    CloseOutReceipt: SETTLED · DTIF + closeoutReceiptCid + settlementTxHash anchored on-chain`, 'success');
          addLog(`[CANTON]    Deterministic T+0 finality — cannot be altered post-settlement · 24/7 settlement-ready`);
        }
      },
      {
        label: 'Mercury → Column N.A. → FedWire RTGS Settlement',
        run: async () => {
          addLog(`[MERCURY] Initiating wire via Mercury Banking API (Column N.A.)...`);
          addLog(`[MERCURY] Originator: KRO Brokers SPC · Account: checking (USD)`);
          addLog(`[MERCURY] Correspondent: Column N.A. · ABA Routing: 121145433 · SWIFT BIC: CLUMUS33XXX`);
          await stepDelay(500);
          addLog(`[MERCURY] Wire instruction payload:`);
          addLog(`[MERCURY]   Amount: $${(notional/1e6).toFixed(1)}M USD`);
          addLog(`[MERCURY]   Beneficiary: [Counterparty Settlement Account]`);
          addLog(`[MERCURY]   Memo: UETR=${uetr.slice(0,13)}... DTIF=ZX18049M0`);
          addLog(`[MERCURY]   Purpose: REPO — Intraday repo credit draw settlement`);
          await stepDelay(600);
          addLog(`[MERCURY] Column N.A. accepted wire instruction → forwarding to FedWire`);
          addLog(`[FEDWIRE] Federal Reserve Wire Network processing...`);
          await stepDelay(500);
          addLog(`[FEDWIRE] RTGS settlement: funds debited from Column N.A. reserve account`);
          addLog(`[FEDWIRE] Federal Reserve reference: ${fedRef} — independently assigned by Fed`);
          addLog(`[FEDWIRE] Settlement finality: IRREVOCABLE — FedWire Funds Service Regulation J`, 'success');
          addLog(`[MERCURY] Bank-to-Customer notification: debit/credit booking confirmed`);
          addLog(`[MERCURY] Settlement status: collateral position SETT (Settled)`);
          addLog(`[MERCURY] Wire traceable: Mercury API GET /transactions?search=${uetr.slice(0,13)}`);
        }
      },
      {
        label: 'Registry Receipt — Finality + Document Key',
        run: async () => {
          addLog(`[RECEIPT] Aggregating cross-rail confirmations from all providers...`);
          await stepDelay(300);
          addLog(`[RECEIPT] Provenance TX:     ${provenanceTxHash.slice(0,16)}... · block ${blockHeight} ✓`);
          addLog(`[RECEIPT] Canton Contract:    ${cantonContractId} · SETTLED ✓`);
          addLog(`[RECEIPT] BitGo TX:           ${bitgoTxHash.slice(0,16)}... · MPC 2/3 ✓`);
          addLog(`[RECEIPT] FedWire Ref:        ${fedRef} · IRREVOCABLE ✓`);
          addLog(`[RECEIPT] UETR:               ${uetr} ✓`);
          addLog(`[RECEIPT] Global Sync:        Cross-sync DvP confirmed · Reassignment complete ✓`);
          addLog(`[RECEIPT] CIP-0056:           Allocation_ExecuteTransfer · All legs consumed ✓`);
          await stepDelay(400);
          addLog(`[RECEIPT] ══════════════════════════════════════════════`, 'key');
          addLog(`[RECEIPT] Public Root Registry Key: ${rootRegistryKey}`, 'key');
          addLog(`[RECEIPT] Registry Sequence: #${registrySeq}`, 'key');
          addLog(`[RECEIPT] ══════════════════════════════════════════════`, 'key');
          await stepDelay(300);
          addLog(`[RECEIPT] AES-256-GCM Document Key: generated for counterparty`);
          addLog(`[RECEIPT] UCC Article 12: control transfer perfected — "perfection by control" confirmed`);
          addLog(`[RECEIPT] Canton finality: deterministic T+0 — legally final and irrevocable`);
          addLog(`[RECEIPT] On-ledger chain: Allocation → Allocation_ExecuteTransfer → CloseOutReceipt — COMPLETE`);
          addLog(`[RECEIPT] KRO role: settlement infrastructure and admin governance — compliant by design`);
          addLog(`[RECEIPT] Trust Index entry: ANCHORED — institutional-grade evidentiary record`);
          addLog(`[RECEIPT] ═══ SANDBOX E2E SETTLEMENT TEST — ALL STAGES PASSED ═══`, 'complete');
        }
      },
    ];

    for (let i = 0; i < steps.length; i++) {
      setSandboxStep(i);
      addLog(`\n── Step ${i + 1}/${steps.length}: ${steps[i].label} ──`, 'header');
      await steps[i].run();
      await stepDelay(400);
    }

    const receipt = {
      testId,
      rootRegistryKey,
      registrySeq,
      uetr,
      cantonContractId,
      provenanceTxHash,
      bitgoTxHash,
      fedRef,
      blockHeight,
      completedAt: new Date().toISOString(),
      steps: steps.map(s => s.label),
    };
    setSandboxReceipt(receipt);
    setSandboxRunning(false);
    setSandboxStep(steps.length);

    try { sessionStorage.setItem('kro_sandbox_receipt', JSON.stringify(receipt)); } catch (_) {}

    // QUARANTINE: the sandbox is a client-side simulation (Math.random / fabricated
    // CIDs, UETRs, block heights). It MUST NOT be injected into feedStatus.events or
    // webhookEvents — those tables render the live canton_event_log and an inbound
    // webhook stream, and mixing simulated rows beside on-ledger data breaks the
    // determinism/underwriteability of the feed. Sandbox results stay in the sandbox
    // panel only, via `sandboxReceipt` and sessionStorage above.
  }, []);

  // ── Dynamic network-tier helpers ─────────────────────────────────────────
  const activeNetwork    = cantonNetworkStatus.network || 'MainNet';
  const isMainnetLive    = activeNetwork === 'MainNet';
  const isTestnetActive  = activeNetwork === 'TestNet';
  const isDevnetActive   = activeNetwork === 'DevNet';

  // Status label shown on DAML modules that are live-deployed vs. sandbox-only
  const damlModuleStatus =
    isMainnetLive   ? 'MainNet Live'   :
    isTestnetActive ? 'TestNet Active' :
    isDevnetActive  ? 'DevNet Active'  : 'Sandbox';

  // Status label shown on Canton workflow stages
  const wfLiveStatus  = isMainnetLive ? 'LIVE'    : isTestnetActive ? 'TESTING' : 'PENDING';
  const wfReadyStatus = isMainnetLive ? 'CREATED' : 'CREATED';

  // Badge color for the network pill
  const networkBadgeColor =
    isMainnetLive   ? '#10b981' :
    isTestnetActive ? '#f59e0b' :
    isDevnetActive  ? '#38bdf8' : '#64748b';

  // Short label for network pill
  const networkPillLabel =
    isMainnetLive   ? '● MAINNET LIVE'   :
    isTestnetActive ? '● TESTNET ACTIVE' :
    isDevnetActive  ? '● DEVNET LIVE'    : '● SANDBOX';

  // ── Live on-ledger presence ──────────────────────────────────────────────
  // Derive the workflow stage badges from the same live canton_event_log feed
  // (feedStatus.events) instead of hardcoding them. When the feed is empty
  // (endpoint unreachable) we fall back to the network-tier labels so the view
  // never regresses to an all-pending state on a transient fetch failure.
  const liveEv      = feedStatus.events || [];
  const hasLiveFeed = liveEv.length > 0;
  const evPresent   = (t) => liveEv.some(e => e.type === t);
  const hasFacility = evPresent('REPO_FACILITY');
  const hasDraw     = evPresent('REPO_DRAW');
  const hasCloseout = evPresent('CLOSEOUT');
  const hasPledge   = evPresent('CANTON_PLEDGE');
  const hasSettle   = evPresent('CANTON_SETTLE') || hasCloseout;
  const liveOr      = (derived, fallback) => (hasLiveFeed ? derived : fallback);

  const views = [
    { id: 'pipeline', label: 'Settlement Pipeline', icon: 'bi-diagram-3-fill' },
    { id: 'marker-ops', label: 'Marker Operations', icon: 'bi-arrow-left-right' },
    { id: 'webhook', label: 'Webhook Feed', icon: 'bi-broadcast' },
    { id: 'canton', label: 'Canton Workflows', icon: 'bi-lightning-charge-fill' },
    { id: 'daml', label: 'DAML Smart Contracts', icon: 'bi-file-earmark-code-fill' },
    { id: 'document-key', label: 'Document Key', icon: 'bi-key-fill' },
    { id: 'affidavit', label: 'Affidavit Data Feed', icon: 'bi-file-earmark-check-fill' },
    { id: 'sandbox-test', label: 'Sandbox E2E Test', icon: 'bi-play-circle-fill' },
    { id: 'verification', label: 'Execution Verification', icon: 'bi-shield-fill-check' },
  ];

  const pipelineSteps = [
    { num: '1', icon: 'bi-file-earmark-arrow-up', label: 'Collateral Adequacy', desc: 'Collateral and exposure check — triggers collateral mobilisation. Reports collateral value, haircut %, eligible securities matrix, and exposure type (REPO). Drawing Institution posts eligible collateral; adequacy hash verified: collateral×(1−haircut) ≥ notional. Must pass before settlement fires.', tag: 'CANTON' },
    { num: '2', icon: 'bi-lock-fill', label: 'CIP-0056 Allocation Lock', desc: 'AllocationFactory_Allocate locks collateral holdings into an Allocation contract per Canton CIP-0056 token standard. Both legs (collateral + cash) are reserved atomically with allocateBefore deadline. Executor party (settlement agent) controls lifecycle. Net amounts locked per CIP-0112 — reduces capital drag by only reserving the delta. Allocation visible only to counterparties (sub-transaction privacy).', tag: 'CANTON' },
    { num: '3', icon: 'bi-safe', label: 'Tri-Party Custody Gate', desc: 'BitGo MPC tri-party custodian independently revalues collateral at real-time oracle price. Haircut re-applied on verified NAV (not stale mark). Concentration limit enforced: single-issuer ≤ 25%. MPC 2-of-3 threshold signature required (HSM share + client policy engine). Collateral Matrix updated in Settlement Hub. Custody approval is a prerequisite for cash release.' },
    { num: '4', icon: 'bi-building-fill-check', label: 'Clearing & Compliance Gate', desc: 'KYC/AML attestation re-verified (GLEIF LEI + KRO Encrypted Virtual ID, modeled on vLEI best practices). Sanctions screening. Modeled on FICC/DTCC GCF repo clearing workflows — KRO does not operate as a CCP or FICC member. Nonconsuming Daml choice keeps the draw active for DvP. Clearing Gate: PASSED required before settlement fires.' },
    { num: '5', icon: 'bi-coin', label: 'CIP-56 Cash Leg', desc: 'On-chain CIP-56 cash token forms the cash leg — reserved in the Allocation and settled atomically with the collateral leg on the Canton Global Synchronizer. No off-ledger instruction required; an optional USD bank rail (Column N.A. → FedWire RTGS) remains available for fiat egress.', tag: 'CANTON' },
    { num: '6', icon: 'bi-cash-stack', label: 'Atomic Settlement Pre-Check', desc: 'Both legs — collateral and on-chain CIP-56 cash — confirmed reserved and ready. Custody (Step 3) and clearing (Step 4) gates must have passed before Canton DvP triggers.', tag: 'CANTON' },
    { num: '7', icon: 'bi-globe2', label: 'Global Synchronizer — Cross-Sync DvP', desc: 'Assets on separate synchronizers are reassigned to the Global Synchronizer for atomic settlement per Canton Foundation open-source cross-sync DvP pattern. Both collateral and cash contracts must reside on the same synchronizer. Reassignment is a two-phase protocol: unassign from source → assign to settlement sync. No value changes hands until the atomic step. KRO applies publicly available Canton standards as settlement infrastructure.', tag: 'CANTON' },
    { num: '8', icon: 'bi-lightning-charge-fill', label: 'Canton Atomic DvP', desc: 'Executor exercises Allocation_ExecuteTransfer on all legs simultaneously. Collateral leg (marker pledge escrow on Provenance — assets remain in vault, KRO retains ownership, counterparty receives qualified purchaser status) and the on-chain CIP-56 cash leg settle in a single indivisible Daml transaction. Both legs settle or both roll back. T+0 deterministic finality. Applies Canton Network open-source atomic DvP standards available to all general members and operators.', tag: 'CANTON' },
    { num: '9', icon: 'bi-arrow-repeat', label: 'Post-DvP Reassignment', desc: 'Optional: assets reassigned back from Global Synchronizer to their home synchronizer (KRO-Mainnet-1 / pio-mainnet-1). Keeps assets on infrastructure best suited for their lifecycle — future transfers, margin calls, corporate actions. Per Canton cross-synchronizer DvP pattern.', tag: 'CANTON' },
    { num: '10', icon: 'bi-patch-check-fill', label: 'Finality — CloseOutReceipt', desc: 'CloseOutReceipt confirms the post-DvP collateral position (SettlementStatus: SETT) and on-ledger booking. UCC Article 12 "perfection by control" confirmed. Document Key (AES-256-GCM) issued to counterparty. Immutable CloseOutReceipt anchored on Canton. Allocation contracts archived. 24/7 settlement-ready.', tag: 'CANTON' },
  ];

  const markerDenoms = [
    { denom: 'kro.gipr.genesis.v2', label: 'Genesis Bond', symbol: 'KRO-GIPR', price: '$1,000,000', units: 60, color: '#f59e0b' },
    { denom: 'kro.as.accountability.v2', label: 'Accountability', symbol: 'KRO-AS', price: '$5,000,000', units: 182, color: '#818cf8' },
    { denom: 'kro.sc.stewardship.v2', label: 'Stewardship Draw', symbol: 'KRO-SC', price: '$20,000,000', units: 12, color: '#10b981' },
  ];

  const cantonWorkflows = [
    { stage: 'PledgeWorkflow',   status: liveOr(hasPledge ? 'CREATED' : 'PENDING', wfReadyStatus), detail: 'Pre-condition: collateral adequacy hash verified against the collateral and exposure check. CER (Crypto-Economic Rights) and DTI (Digital Token Identifier) anchors validated on Provenance. CIP-0056 AllocationFactory_Allocate locks collateral holdings with allocateBefore deadline. Both Provider and Receiver signatories must authorize. Net amounts locked per CIP-0112.', phase: 'Stage 1 of 5 — Collateral Pledge + Allocation Lock' },
    { stage: 'DrawWorkflow',     status: liveOr(hasDraw ? 'LIVE' : 'PENDING', wfLiveStatus),  detail: 'Treasury draw initiated — custody (BitGo MPC) and clearing gates must pass first. The on-chain CIP-56 cash leg is reserved in the Allocation only after gates clear. Facility usedCapacity incremented. KRO never takes custody of funds. Canton verifies CustodianApproveCollateral + ClearingAgentApprove choices were exercised before draw.', phase: 'Stage 2 of 5 — Gated Cash Draw' },
    { stage: 'GlobalSync DvP',   status: liveOr(hasSettle ? 'LIVE' : 'PENDING', wfLiveStatus),  detail: 'Collateral and cash contracts reassigned to Canton Global Synchronizer for cross-sync atomic settlement. BFT consensus via Global Synchronizer. Executor exercises Allocation_ExecuteTransfer: collateral leg (marker transfer) ↔ on-chain CIP-56 cash leg — single indivisible Daml transaction. Both legs settle simultaneously or entire transaction rolls back. Sub-transaction privacy preserved. KRO role: settlement infrastructure and admin governance — not a counterparty or clearing agent.', phase: 'Stage 3 of 5 — Atomic DvP via Global Synchronizer' },
    { stage: 'Margin & Lifecycle', status: 'MONITORING', detail: 'During open repo: automated margin monitoring (15-min revaluation cycle). MarginCall choice fires if exposure exceeds threshold. Collateral substitution available (SubstituteCollateral choice — atomic swap, custodian re-verification required). Variation margin settled on Canton without manual reconciliation. Repo interest accrues: notional × rate × (ACT/360). Assets reassigned back to home synchronizers after DvP.', phase: 'Stage 4 of 5 — Lifecycle Management' },
    { stage: 'CloseOutReceipt',  status: liveOr(hasCloseout ? 'SETTLED' : 'PENDING', isMainnetLive ? 'SETTLED' : wfLiveStatus), detail: 'ReturnFunds → capacity regenerated. Repo interest settled atomically at maturity (ACT/360 convention). Immutable CloseOutReceipt anchored on Canton ledger. Contains: CloseOutReceipt CID, UETR, settlementTxHash (Provenance), Canton block height, DTIF registry ID, repo interest paid, and Document Key reference. Allocation contracts archived. This is the finality proof — legally binding under Canton consensus, irrevocable post-settlement. 24/7 settlement-ready.', phase: 'Stage 5 of 5 — Finality Receipt' },
  ];

  const repoWorkflows = [
    {
      stage: 'IntradayRepoFacility',
      status: liveOr(hasFacility ? 'ACTIVE' : 'PENDING', 'ACTIVE'),
      detail: 'Revolving intraday repo facility — $910M accountability markers (kro.as.accountability.v2), $10M per draw, 2% initial margin. Settlement protocol enforced by Daml smart contracts on Canton Network. KRO acts as steward of private-wealth clients, presenting a standard RFQ (request-for-quote) service request to an approved settlement provider for price discovery — the provider (respondent) reviews, submits an indicative quote, and accepts via AcceptRFP (the respondent is the AcceptRFP controller). This is a standard account/service trigger on standard RFQ terms, not a partnership ask. Instrument left open for the provider\'s standard classification. Upon the provider\'s acceptance at the quoted price, the standard RFQ service terms govern the settlement. KRO retains full ownership of all assets — collateral is pledged via escrow and never leaves custody vault. Counterparty becomes qualified purchaser with a security interest. No re-hypothecation, no default seizure — atomic DvP pattern ensures both legs settle or both roll back per Canton standards. Parties: Collateral Provider (respondent, regulated FI) + Collateral Receiver (requestor, drawing institution) + Custodian (BitGo MPC, tri-party) + kroOS (observer ONLY — no execution authority). Facility regenerates capacity intraday upon ReturnFunds. 24/7 settlement-ready.',
      phase: 'Facility Layer — Daml Protocol-Enforced',
      icon: 'bi-bank2',
      color: '#f59e0b',
    },
    {
      stage: 'OpenDraw — IntradayRepoDraw',
      status: liveOr(hasDraw ? 'OPEN' : 'AWAITING_PAYMENT', 'AWAITING_PAYMENT'),
      detail: 'Collateral Receiver initiates draw against facility. DTIF Registry ID stamped per ISO 24165. Pre-condition enforced by Daml: collateral*(1−haircutPct) ≥ notional. Eligible collateral types defined in Collateral Matrix. Draw blocked if facility limit exceeded or collateral ineligible. CIP-0056 AllocationFactory_Allocate locks holdings with allocateBefore deadline — net amounts only per CIP-0112. Pilot parameters: $10M cap per transaction, max 2 transactions/day, 10-day pilot window, kro.as.accountability.v2 credits only.',
      phase: 'Stage 1 of 7 — Pre-condition + Allocation Lock',
      icon: 'bi-file-earmark-arrow-up',
      color: '#818cf8',
    },
    {
      stage: 'CustodianApproveCollateral',
      status: liveOr(hasDraw ? 'CREATED' : 'PENDING', 'CREATED'),
      detail: 'BitGo MPC tri-party custodian revalues collateral at real-time oracle price. Haircut re-applied on verified NAV (not stale mark). Concentration limit check: single-issuer ≤ 25% of total collateral pool. Collateral Matrix updated in Settlement Hub. MPC 2-of-3 threshold signature required — hardware HSM share + client policy engine.',
      phase: 'Stage 2 of 7 — Tri-Party Custody Gate',
      icon: 'bi-safe',
      color: '#10b981',
    },
    {
      stage: 'ClearingAgentApprove',
      status: liveOr(hasDraw ? 'CREATED' : 'PENDING', 'CREATED'),
      detail: 'Clearing gate modeled on FICC/DTCC GCF repo clearing workflows — KRO does not operate as a CCP or FICC member. Nonconsuming Daml choice (draw contract remains active for DvP). AML/KYC attestation re-verified. Sanctions screening. Clearing Gate: PASSED.',
      phase: 'Stage 3 of 7 — Clearing & Compliance',
      icon: 'bi-building-fill-check',
      color: '#10b981',
    },
    {
      stage: 'Global Synchronizer DvP',
      status: liveOr(hasSettle ? 'RELEASED' : 'PENDING', 'RELEASED'),
      detail: 'Assets reassigned to Canton Global Synchronizer for cross-sync atomic settlement. BFT consensus across Super Validator set (DTCC, Broadridge, Tradeweb, Nasdaq among 42 validators). Executor exercises Allocation_ExecuteTransfer — collateral and on-chain CIP-56 cash legs settle in a single indivisible Daml transaction. Both legs settle or both roll back. Sub-transaction privacy preserved: trade details visible only to counterparties.',
      phase: 'Stage 4 of 7 — Atomic DvP via Global Sync',
      icon: 'bi-lightning-charge-fill',
      color: '#38bdf8',
    },
    {
      stage: 'Margin Call & Variation Margin',
      status: 'MONITORING',
      detail: 'Automated intraday margin monitoring — oracle price feed triggers revaluation every 15 minutes during open draw. If mark-to-market exposure exceeds maintenance margin threshold (collateral NAV − notional < marginBuffer), a MarginCall Daml choice fires automatically. Collateral Receiver must post additional collateral or the facility enters remediation. Variation margin settled atomically via Canton — no manual reconciliation. Applies collateral management best practices published by Canton Foundation for general members.',
      phase: 'Stage 5 of 7 — Continuous Margin Automation',
      icon: 'bi-graph-up-arrow',
      color: '#f59e0b',
    },
    {
      stage: 'Collateral Substitution',
      status: 'AVAILABLE',
      detail: 'During the life of the repo, the Collateral Receiver may substitute pledged collateral with equivalent eligible assets — subject to custodian approval (BitGo MPC re-verification required). SubstituteCollateral is a nonconsuming Daml choice: original collateral released, replacement locked in a single atomic transaction. New collateral must satisfy: eligible asset type + haircut adequacy + concentration limits. Standard tri-party repo feature — eliminates operational drag of full close-out/reopen.',
      phase: 'Stage 6 of 7 — Collateral Optimization',
      icon: 'bi-arrow-left-right',
      color: '#818cf8',
    },
    {
      stage: 'ReturnFunds → CloseOutReceipt',
      status: liveOr(hasCloseout ? 'SETTLED' : 'PENDING', 'SETTLED'),
      detail: 'Collateral Receiver returns funds intraday. usedCapacity −= notional → facility capacity fully regenerated (revolving). Repo interest calculated: notional × repoRate × (actualDays / 360) — settled atomically at maturity. Pilot: notional capped at $10M, day count convention ACT/360, accountability credits only. Assets reassigned back from Global Synchronizer to home synchronizers. Immutable CloseOutReceipt: SETTLED. On-chain anchors: DTIF registry ID + CloseOutReceipt CID + UETR + settlementTxHash (Provenance) + Canton block height. Document Key (AES-256) issued to counterparty. Trust Index entry finalized.',
      phase: 'Stage 7 of 7 — Settlement Finality',
      icon: 'bi-patch-check-fill',
      color: '#a78bfa',
    },
  ];

  /* ── KRO Exchange Registry — TTD Portal Registry Entry ── */
  const KROExchangeRegistry = () => {
    let receipt = sandboxReceipt;
    if (!receipt) {
      try { const raw = sessionStorage.getItem('kro_sandbox_receipt'); if (raw) receipt = JSON.parse(raw); } catch (_) {}
    }

    return (
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(201,168,76,0.2)' }}>
        <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, #1c1208, #0f172a)', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-journal-bookmark-fill" style={{ color: '#c9a84c', fontSize: '1.3rem' }}></i>
            <div>
              <h5 className="mb-0 fw-bold" style={{ color: '#e8d5a0' }}>KRO Exchange Registry</h5>
              <small style={{ color: 'rgba(201,168,76,0.6)' }}>TTD Portal — Public Registry of Settlement Receipts</small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <a href={`${TTD_PORTAL_BASE}?page=portfolio`} target="_blank" rel="noopener noreferrer"
              className="btn btn-sm fw-bold" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontSize: '0.72rem' }}>
              <i className="bi bi-box-arrow-up-right me-1"></i>Open TTD Portal
            </a>
          </div>
        </div>

        <div className="card-body p-3">
          {!receipt ? (
            <div className="text-center py-4" style={{ color: '#475569' }}>
              <i className="bi bi-journal-x" style={{ fontSize: '2rem', opacity: 0.4 }}></i>
              <p className="mt-2 mb-0" style={{ fontSize: '0.82rem' }}>No registry entries yet. Run the Sandbox E2E Test to generate a registry receipt.</p>
            </div>
          ) : (
            <>
              {/* Registry Entry Card */}
              <div className="rounded-3 overflow-hidden mb-3" style={{ border: '1.5px solid rgba(201,168,76,0.3)' }}>
                <div className="p-3" style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.06), rgba(15,23,42,0.9))' }}>
                  {/* Header row */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <div>
                      <div style={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '2px', color: '#c9a84c', textTransform: 'uppercase' }}>Public Registry Entry</div>
                      <div className="fw-bold" style={{ fontSize: '0.95rem', color: '#e8d5a0' }}>Settlement Receipt #{receipt.registrySeq}</div>
                    </div>
                    <div className="d-flex gap-2">
                      <span className="badge bg-success px-2 py-1"><i className="bi bi-check-circle-fill me-1"></i>SETTLED</span>
                      <span className="badge px-2 py-1" style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }}>{receipt.testId}</span>
                    </div>
                  </div>

                  {/* Root Registry Key — hero */}
                  <div className="p-3 rounded-3 mb-3 text-center" style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)' }}>
                    <div style={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '2px', color: '#a78bfa', textTransform: 'uppercase' }}>Public Root Registry Key</div>
                    <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#e9d5ff', fontFamily: 'monospace', letterSpacing: '1.5px', wordBreak: 'break-all' }}>
                      {receipt.rootRegistryKey}
                    </div>
                    <div style={{ fontSize: '0.6rem', color: '#64748b', marginTop: 4 }}>Auto-generated · Unique per settlement · Linked to Document Key</div>
                  </div>

                  {/* Registry fields */}
                  <div className="row g-2 mb-3">
                    {[
                      { label: 'Registry Sequence', value: `#${receipt.registrySeq}`, icon: 'bi-hash' },
                      { label: 'Settlement Date', value: new Date(receipt.completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), icon: 'bi-calendar-check' },
                      { label: 'Settlement Time', value: new Date(receipt.completedAt).toLocaleTimeString(), icon: 'bi-clock' },
                      { label: 'Settlement Type', value: 'Intraday Repo Credit Draw — Canton DvP', icon: 'bi-lightning-charge-fill' },
                      { label: 'Marker Denom', value: 'kro.as.accountability.v2', icon: 'bi-tag-fill' },
                      { label: 'Chain', value: 'Provenance pio-mainnet-1 + Canton Network', icon: 'bi-hdd-network-fill' },
                      { label: 'FedWire Reference', value: receipt.fedRef, icon: 'bi-building-fill' },
                      { label: 'UETR', value: receipt.uetr, icon: 'bi-file-earmark-code' },
                    ].map(f => (
                      <div key={f.label} className="col-md-6 col-lg-3">
                        <div className="p-2 rounded" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)' }}>
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <i className={`bi ${f.icon}`} style={{ color: '#c9a84c', fontSize: '0.65rem' }}></i>
                            <small style={{ color: '#64748b', fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{f.label}</small>
                          </div>
                          <div style={{ color: '#e2e8f0', fontSize: '0.75rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>{f.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Cross-rail references */}
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {[
                      { k: 'Canton', v: receipt.cantonContractId },
                      { k: 'Provenance TX', v: `${receipt.provenanceTxHash?.slice(0,12)}...${receipt.provenanceTxHash?.slice(-6)}` },
                      { k: 'Block', v: receipt.blockHeight },
                      { k: 'BitGo TX', v: `${receipt.bitgoTxHash?.slice(0,12)}...${receipt.bitgoTxHash?.slice(-6)}` },
                      { k: 'CloseOut CID', v: receipt.closeoutReceiptCid },
                    ].map(ref => (
                      <span key={ref.k} className="badge" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)', color: '#c9a84c', fontSize: '0.62rem' }}>
                        {ref.k}: <code style={{ color: '#e8d5a0', fontSize: '0.62rem' }}>{ref.v}</code>
                      </span>
                    ))}
                  </div>

                  {/* View in Studio link */}
                  <div className="p-2 rounded d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      <i className="bi bi-eye-fill text-primary me-1"></i>
                      <strong className="text-white">Approved View:</strong> Full transaction details, Document Key, and execution proof available in Trust Tech Studio.
                    </div>
                    <a href="/kro-exchange/settlement-hub" className="btn btn-sm btn-outline-primary" style={{ fontSize: '0.68rem', padding: '0.15rem 0.5rem' }}>
                      <i className="bi bi-box-arrow-in-right me-1"></i>Open in Studio → Settlement Hub
                    </a>
                  </div>
                </div>
              </div>

              {/* Registry notice */}
              <div className="p-2 rounded" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.6 }}>
                <i className="bi bi-info-circle" style={{ color: '#c9a84c' }}></i>{' '}
                This registry entry is synced to the TTD Portal KRO Exchange. The Root Registry Key
                <code style={{ color: '#c9a84c', margin: '0 4px' }}>{receipt.rootRegistryKey}</code>
                is the public anchor. Receipt <strong className="text-white">#{receipt.registrySeq}</strong> is auto-generated.
                Transaction details require approved access via Trust Tech Studio (Settlement Hub → Dashboard → Receipt or Execution Gateway → Document Key).
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  /* ── Execution Flow Confirmation Panel (inline) ── */
  const ExecutionFlowPanel = () => {
    const [providers, setProviders] = useState(EXECUTION_PROVIDERS.map(p => ({ ...p, status: 'confirmed' })));
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    const sColor = (s) => s === 'confirmed' ? '#10b981' : s === 'checking' ? '#f59e0b' : '#ef4444';
    const bColor = (b) => b === 'CONFIRMED' ? 'success' : b === 'READY' ? 'info' : b === 'CONNECTED' ? 'primary' : b === 'DEGRADED' ? 'danger' : 'warning';
    const allOk = providers.every(p => p.status === 'confirmed');

    const doRefresh = () => {
      setRefreshing(true);
      setProviders(prev => prev.map(p => ({ ...p, status: 'checking', badge: 'CHECKING' })));
      setTimeout(() => {
        setProviders(EXECUTION_PROVIDERS.map(p => ({ ...p, status: 'confirmed' })));
        setRefreshing(false);
        setLastRefresh(new Date());
      }, 2200);
    };

    return (
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(16,185,129,0.2)' }}>
        <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, #064e3b, #0f172a)', borderBottom: '1px solid rgba(16,185,129,0.15)' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-patch-check-fill text-success fs-4"></i>
            <div>
              <h5 className="mb-0 fw-bold text-white">Execution Flow Confirmation</h5>
              <small className="text-white-50">Third-party DvP stack verification — 5 sequential provider confirmations</small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className={`badge bg-${allOk ? 'success' : 'warning'} px-3 py-2 fs-6`}>
              <i className={`bi bi-${allOk ? 'check-circle-fill' : 'exclamation-triangle-fill'} me-1`}></i>
              {allOk ? 'ALL SYSTEMS READY' : 'PARTIAL'}
            </span>
            <span className="badge bg-success px-2 py-1">
              <i className="bi bi-circle-fill me-1 efcp-pulse" style={{ fontSize: '0.4rem', verticalAlign: 'middle' }}></i>LIVE
            </span>
          </div>
        </div>

        {/* Summary strip */}
        <div className="px-3 py-2 d-flex flex-wrap gap-3 align-items-center" style={{ background: 'rgba(16,185,129,0.05)', borderBottom: '1px solid rgba(16,185,129,0.1)' }}>
          {providers.map(p => (
            <div key={p.id} className="d-flex align-items-center gap-1">
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: sColor(p.status), display: 'inline-block', boxShadow: `0 0 6px ${sColor(p.status)}` }}></span>
              <span className="text-white-50" style={{ fontSize: '0.72rem', fontWeight: 600 }}>{p.name.split('—')[0].split('→')[0].trim()}</span>
            </div>
          ))}
          <div className="ms-auto d-flex align-items-center gap-2">
            <small className="text-white-50" style={{ fontSize: '0.68rem' }}>Last: {lastRefresh.toLocaleTimeString()}</small>
            <button className="btn btn-sm btn-outline-success" onClick={doRefresh} disabled={refreshing} style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}>
              <i className={`bi bi-arrow-clockwise me-1 ${refreshing ? 'efcp-spin' : ''}`}></i>Refresh
            </button>
          </div>
        </div>

        <div className="card-body p-3">
          <div className="d-flex flex-column gap-3">
            {providers.map((p, idx) => (
              <div key={p.id} className="rounded-3 p-3" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${sColor(p.status)}30` }}>
                <div className="d-flex align-items-start gap-3">
                  <div className="d-flex flex-column align-items-center" style={{ minWidth: 36 }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 36, height: 36, background: `${sColor(p.status)}15`, border: `1.5px solid ${sColor(p.status)}40` }}>
                      <i className={`bi ${p.icon}`} style={{ color: sColor(p.status), fontSize: '1rem' }}></i>
                    </div>
                    <span className="text-white-50 fw-bold mt-1" style={{ fontSize: '0.65rem' }}>STEP {idx + 1}</span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                      <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>{p.name}</span>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: sColor(p.status), display: 'inline-block', boxShadow: `0 0 6px ${sColor(p.status)}` }}></span>
                      <span className={`badge bg-${bColor(p.badge)} ms-1`} style={{ fontSize: '0.65rem' }}>{p.badge}</span>
                    </div>
                    <p className="text-white-50 mb-2" style={{ fontSize: '0.78rem', lineHeight: 1.5 }}>{p.detail}</p>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {Object.entries(p.refs).map(([k, v]) => (
                        <span key={k} className="badge" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#94a3b8', fontSize: '0.65rem', fontWeight: 500 }}>
                          {k}: <code style={{ color: '#e2e8f0', fontSize: '0.65rem' }}>{v}</code>
                        </span>
                      ))}
                    </div>
                    <div className="mt-2">
                      {p.verifyUrl ? (
                        <a href={p.verifyUrl} target="_blank" rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-info" style={{ fontSize: '0.65rem', padding: '0.15rem 0.5rem' }}>
                          <i className="bi bi-box-arrow-up-right me-1"></i>{p.verifyLabel}
                        </a>
                      ) : p.verify ? (
                        <div className="d-flex align-items-start gap-1" style={{ fontSize: '0.65rem', color: '#64748b' }}>
                          <i className="bi bi-terminal-fill flex-shrink-0 mt-1" style={{ color: '#818cf8' }}></i>
                          <code style={{ color: '#94a3b8', fontSize: '0.65rem', wordBreak: 'break-word' }}>{p.verify}</code>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Third-Party Execution Confirmation notice */}
          <div className="mt-3 p-3 rounded-3" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
            <div className="d-flex align-items-start gap-2">
              <i className="bi bi-info-circle-fill text-success mt-1" style={{ fontSize: '0.9rem' }}></i>
              <div>
                <strong className="text-white d-block mb-1" style={{ fontSize: '0.82rem' }}>Third-Party Execution Confirmation</strong>
                <p className="text-white-50 mb-1" style={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
                  This panel confirms live connectivity and authorisation across all five independent third-party providers.
                  Each provider operates autonomously — KRO does not custody, clear, or settle funds. Cross-rail references bind the execution chain:
                </p>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {[
                    { label: 'Canton Contract ID', value: 'DrawWorkflow#12a4' },
                    { label: 'BitGo TX Hash', value: '7A3F...E91B' },
                    { label: 'DTIF', value: 'ZX18049M0' },
                    { label: 'UETR', value: 'a1b2c3d4...7890' },
                    { label: 'CloseOut CID', value: '00c2db19...8554' },
                  ].map(ref => (
                    <span key={ref.label} className="badge" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#6ee7b7', fontSize: '0.65rem' }}>
                      {ref.label}: <code style={{ color: '#a7f3d0', fontSize: '0.65rem' }}>{ref.value}</code>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container-fluid py-4" style={{ background: '#0f172a', minHeight: '100vh' }}>

      {/* ── Attestation API unavailable banner ── */}
      {attestationApiStatus !== null && attestationApiStatus !== 200 && (
        <div className="alert d-flex align-items-start gap-3 mb-3 border-0"
          style={{ background: '#431407', borderLeft: '4px solid #f97316', color: '#fed7aa', borderRadius: '8px' }}>
          <i className="bi bi-exclamation-triangle-fill fs-5 flex-shrink-0 mt-1" style={{ color: '#fb923c' }}></i>
          <div className="flex-grow-1">
            <div className="fw-bold mb-1" style={{ color: '#fff' }}>
              Attestation API Unavailable — HTTP {attestationApiStatus || 'Network Error'}
            </div>
            <div style={{ fontSize: '0.83rem' }}>
              The backend endpoint <code style={{ color: '#fdba74' }}>/api/v1/attestation/*</code> on{' '}
              <code style={{ color: '#fdba74' }}>ca-api-prod-wus2</code> is returning server errors.
              The affidavit form is operating in <strong>offline mode</strong> — data entered here can be saved
              locally but will not sync to KRO Marketplace until the backend is restored.
              Check Azure Portal → Container Apps → Logs to diagnose.
            </div>
          </div>
          <button className="btn btn-sm flex-shrink-0" onClick={fetchAffidavit}
            style={{ background: '#9a3412', color: '#fed7aa', border: '1px solid #c2410c', fontSize: '0.75rem' }}>
            <i className="bi bi-arrow-clockwise me-1"></i>Retry
          </button>
        </div>
      )}

      {/* Header */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)' }}>
        <div className="card-body py-4 text-white">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h2 className="mb-1 fw-bold">
                <i className="bi bi-lightning-fill text-warning me-2"></i>Execution Gateway
              </h2>
              <p className="mb-0 opacity-75" style={{ fontSize: '0.9rem' }}>
                Canton Settlement Pipeline · TTD Portal Data Ingestion · CIP-56 Atomic DvP · Marker Operations
              </p>
            </div>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <span className={`badge ${feedStatus.connected ? 'bg-success' : 'bg-danger'} d-flex align-items-center gap-1`}>
                <i className={`bi bi-circle-fill`} style={{ fontSize: '0.5rem' }}></i>
                {feedStatus.connected ? 'TTD Feed Connected' : 'Disconnected'}
              </span>
              <a href={TTD_PORTAL_BASE} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-light fw-bold">
                <i className="bi bi-box-arrow-up-right me-1"></i>TTD Portal
              </a>
              <button className="btn btn-sm btn-success fw-bold" onClick={syncFeedStatus}>
                <i className="bi bi-arrow-repeat me-1"></i>Sync
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        {views.map(v => (
          <button
            key={v.id}
            className={`btn btn-sm fw-bold ${activeView === v.id ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setActiveView(v.id)}
          >
            <i className={`bi ${v.icon} me-1`}></i>{v.label}
          </button>
        ))}
      </div>

      {/* Settlement Pipeline View */}
      {activeView === 'pipeline' && (
        <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b' }}>
          <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-diagram-3-fill fs-4 text-warning me-2"></i>
              <div>
                <h5 className="mb-0 fw-bold text-white">Canton Settlement Pipeline</h5>
                <small className="text-white opacity-75">Collateral Adequacy → CIP-0056 Lock → Custody → Clearing → CIP-56 Cash Leg → Pre-Check → Global Sync → DvP → Reassign → Finality</small>
              </div>
            </div>
          </div>
          <div className="card-body p-4">
            <div className="d-flex flex-column gap-2 mb-4" style={{ maxWidth: '700px', margin: '0 auto' }}>
              {pipelineSteps.map((item, i, arr) => (
                <React.Fragment key={i}>
                  <div className="p-3 rounded border text-start w-100" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)' }}>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-bold text-primary">{item.num}.</span>
                      <i className={`bi ${item.icon} text-success`}></i>
                      <code className="fw-bold text-light">{item.label}</code>
                      {item.tag && <span className="badge ms-auto" style={{ fontSize: '0.6rem', background: '#38bdf822', color: '#38bdf8' }}>{item.tag}</span>}
                    </div>
                    <p className="mb-0 ms-4 mt-1" style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{item.desc}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="text-center"><i className="bi bi-arrow-down" style={{ color: '#475569' }}></i></div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="text-center">
              <a href={`${TTD_PORTAL_BASE}?page=portfolio`} target="_blank" rel="noopener noreferrer" className="btn btn-success">
                <i className="bi bi-box-arrow-up-right me-1"></i>View Settlement in TTD Portal
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Marker Operations View */}
      {activeView === 'marker-ops' && (
        <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b' }}>
          <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <i className="bi bi-arrow-left-right fs-4 text-white me-2"></i>
                <div>
                  <h5 className="mb-0 fw-bold text-white">Marker Assignment — Escrow-Governed Atomic Settlement</h5>
                  <small className="text-white opacity-75">This IS the pilot — how markers move and how we assign private wealth rights</small>
                </div>
              </div>
              <span className="badge bg-white text-success fw-bold">PILOT CORE</span>
            </div>
          </div>
          <div className="card-body p-4">
            <p style={{ fontSize: '0.88rem', color: '#94a3b8', lineHeight: 1.7, maxWidth: '700px' }}>
              Sets the marker denom, assignee Provenance wallet, units, and price. On fulfillment: markers transfer atomically to the assignee, USDT moves to the seller wallet via Canton DvP.
            </p>

            <h6 className="fw-bold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              Active Pilot Markers — pio-mainnet-1
            </h6>
            <div className="row g-3 mb-4">
              {markerDenoms.map(({ denom, label, symbol, price, units, color }) => (
                <div key={denom} className="col-md-4">
                  <div className="card border-0 h-100" style={{ background: '#0f172a' }}>
                    <div className="card-body p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="rounded-circle" style={{ width: 8, height: 8, background: '#10b981', display: 'inline-block', boxShadow: '0 0 6px #10b981', flexShrink: 0 }}></span>
                        <span className="badge" style={{ background: `${color}22`, color, fontSize: '0.65rem', fontWeight: 800 }}>{symbol}</span>
                      </div>
                      <div className="fw-bold text-white mb-1" style={{ fontSize: '0.85rem' }}>{label}</div>
                      <div className="mb-1" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{price} · {units} units</div>
                      <code style={{ fontSize: '0.65rem', color: '#94a3b8', wordBreak: 'break-all' }}>{denom}</code>
                      <div className="mt-2">
                        <a href={`https://zonescan.io/provenance/assets/${denom}`} target="_blank" rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-secondary w-100" style={{ fontSize: '0.72rem', padding: '0.2rem' }}>
                          Verify on ZoneScan ↗
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <a href={TTD_PORTAL_BASE} target="_blank" rel="noopener noreferrer" className="btn btn-success btn-sm fw-bold">
                <i className="bi bi-box-arrow-up-right me-1"></i>Open Marker Assignment in TTD Portal
              </a>
              <a href="https://zonescan.io/provenance/assets/kro.as.accountability.v2" target="_blank" rel="noopener noreferrer"
                className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.78rem' }}>
                <i className="bi bi-box-arrow-up-right me-1"></i>Verify on ZoneScan
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Feed View */}
      {activeView === 'webhook' && (
        <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b' }}>
          <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <i className="bi bi-broadcast fs-4 text-warning me-2"></i>
                <div>
                  <h5 className="mb-0 fw-bold text-white">Marker Lifecycle — Webhook Events</h5>
                  <small className="text-white opacity-75">Real-time Mint / Transfer / Withdraw / Burn from pio-mainnet-1</small>
                </div>
              </div>
              <a href="https://www.krostudio.org" target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-light fw-bold">
                <i className="bi bi-box-arrow-up-right me-1"></i>Trust Tech Studio
              </a>
            </div>
          </div>
          <div className="card-body p-4">
            {/* Event type indicators */}
            <div className="row g-3 mb-4">
              {[
                { event: 'MINT', icon: 'bi-link-45deg', color: '#10b981', desc: 'New marker created. Finalize → Activate → Grant.' },
                { event: 'TRANSFER', icon: 'bi-arrow-left-right', color: '#818cf8', desc: 'Atomic marker transfer. Escrow-governed via Canton DvP.' },
                { event: 'WITHDRAW', icon: 'bi-box-arrow-up', color: '#f59e0b', desc: 'Marker withdrawn from custody. Triggers Document Key.' },
                { event: 'BURN', icon: 'bi-fire', color: '#ef4444', desc: 'Marker destroyed. Reconciliation anchored in Canton.' },
              ].map(({ event, icon, color, desc }) => (
                <div key={event} className="col-md-3 col-sm-6">
                  <div className="p-3 rounded-3 h-100" style={{ background: `${color}0a`, border: `1px solid ${color}33` }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className={`bi ${icon}`} style={{ color, fontSize: '1.2rem' }}></i>
                      <span className="badge fw-bold" style={{ background: `${color}22`, color, fontSize: '0.7rem' }}>{event}</span>
                    </div>
                    <p className="mb-0" style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.5 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Live webhook events table */}
            <h6 className="fw-bold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              Recent Webhook Events (TTD Portal → Studio)
            </h6>
            <div className="table-responsive">
              <table className="table table-dark table-hover align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155' }}>
                    <th style={{ color: '#94a3b8' }}>Event</th>
                    <th style={{ color: '#94a3b8' }}>Denom</th>
                    <th style={{ color: '#94a3b8' }}>Block</th>
                    <th style={{ color: '#94a3b8' }}>TX Hash</th>
                    <th style={{ color: '#94a3b8' }}>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {webhookEvents.map((ev, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                      <td>
                        <span className={`badge bg-${ev.event === 'MINT' ? 'success' : ev.event === 'TRANSFER' ? 'primary' : ev.event === 'WITHDRAW' ? 'warning' : 'danger'}`}>
                          {ev.event}
                        </span>
                      </td>
                      <td><code style={{ color: '#e2e8f0' }}>{ev.denom}</code></td>
                      <td style={{ color: '#94a3b8' }}>{ev.block.toLocaleString()}</td>
                      <td><code style={{ color: '#818cf8' }}>{ev.txHash}</code></td>
                      <td style={{ color: '#64748b' }}>{new Date(ev.ts).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Canton Workflows View */}
      {activeView === 'canton' && (
        <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b' }}>
          <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <i className="bi bi-lightning-charge-fill fs-4 text-info me-2"></i>
                <div>
                  <h5 className="mb-0 fw-bold text-white">Canton Daml Workflows</h5>
                  <small className="text-white opacity-75">
                    {cantonTab === 'pcp'
                      ? `4-Stage Settlement: Pledge → Draw → Settlement → CloseOut · ${networkPillLabel}`
                      : cantonTab === 'repo'
                      ? 'Intraday Repo Facility: OpenDraw → BitGo MPC → Clearing → DvP → CloseOut'
                      : `Live ${activeNetwork} Ledger — kro-settlement v${cantonNetworkStatus.packageVersion || '0.1.3'}`}
                  </small>
                </div>
              </div>
              {/* Toggle */}
              <div className="d-flex gap-2">
                <button
                  className={`btn btn-sm fw-bold ${cantonTab === 'pcp' ? 'btn-info' : 'btn-outline-secondary'}`}
                  onClick={() => setCantonTab('pcp')}
                >
                  <i className="bi bi-diagram-3 me-1"></i>PCP Contracts
                </button>
                <button
                  className={`btn btn-sm fw-bold ${cantonTab === 'repo' ? 'btn-warning' : 'btn-outline-secondary'}`}
                  onClick={() => setCantonTab('repo')}
                >
                  <i className="bi bi-bank2 me-1"></i>Intraday Repo
                </button>
                <button
                  className={`btn btn-sm fw-bold ${cantonTab === 'ledger' ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => {
                    setCantonTab('ledger');
                    if (!cantonLedger.loaded) {
                      setCantonLedger(s => ({ ...s, loading: true }));
                      // Follow the live active network rather than a hardcoded
                      // env=devnet, so the Live Ledger tab reflects the same posture
                      // (/canton/summary network tier) as the rest of the hub.
                      const ledgerEnv = CANTON_ENV;
                      Promise.all([
                        fetch(`${KRO_API_BASE}/canton/summary`).then(r => r.json()).catch(() => null),
                        fetch(`${KRO_API_BASE}/canton/contracts?env=${ledgerEnv}&limit=20`).then(r => r.json()).catch(() => null),
                      ]).then(([summary, contracts]) => {
                        const allRecords = contracts?.success ? contracts.records || [] : [];
                        setCantonLedger({
                          summary: summary?.success ? summary.summary : null,
                          contracts: allRecords.filter(r =>
                            !r.event_type?.includes('EthicsFund') && !r.template_id?.includes('EthicsFund')
                          ),
                          loading: false,
                          loaded: true,
                        });
                      });
                    }
                  }}
                >
                  <i className="bi bi-hdd-network me-1"></i>⛓ Live Ledger
                </button>
              </div>
            </div>
          </div>

          <div className="card-body p-4">

            {/* PCP Contracts */}
            {cantonTab === 'pcp' && (
              <div className="d-flex flex-column gap-3">
                {cantonWorkflows.map((wf, idx) => {
                  const wfColor =
                    wf.status === 'LIVE'    ? '#10b981' :
                    wf.status === 'SETTLED' ? '#a78bfa' :
                    wf.status === 'TESTING' ? '#f59e0b' :
                    wf.status === 'CREATED' ? '#10b981' :
                    wf.status === 'MONITORING' ? '#38bdf8' : '#64748b';
                  const wfIcon =
                    wf.status === 'LIVE'    ? 'bi-lightning-charge-fill' :
                    wf.status === 'SETTLED' ? 'bi-patch-check-fill' :
                    wf.status === 'TESTING' ? 'bi-play-circle-fill' :
                    wf.status === 'CREATED' ? 'bi-check-circle-fill' :
                    wf.status === 'MONITORING' ? 'bi-graph-up-arrow' : 'bi-hourglass-split';
                  return (
                  <div key={idx} className="p-3 rounded-3 border d-flex align-items-center gap-3"
                    style={{ borderColor: `${wfColor}44`, background: `${wfColor}06` }}>
                    <div className="p-2 rounded-3 flex-shrink-0" style={{ background: `${wfColor}18` }}>
                      <i className={`bi ${wfIcon}`} style={{ fontSize: '1.3rem', color: wfColor }}></i>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <h6 className="mb-0 fw-bold text-white" style={{ fontSize: '0.9rem' }}>{wf.stage}</h6>
                        <span className="badge" style={{ background: `${wfColor}18`, color: wfColor, fontSize: '0.6rem' }}>
                          {wf.status}
                        </span>
                        <span style={{ fontSize: '0.62rem', color: '#475569' }}>{wf.phase}</span>
                      </div>
                      <p className="mb-0 mt-1" style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{wf.detail}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}

            {/* Intraday Repo Contracts */}
            {cantonTab === 'repo' && (
              <>
                {/* Facility capacity bar */}
                <div className="p-3 rounded-3 mb-3" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <span style={{ fontSize: '0.72rem', fontWeight: '800', color: '#f59e0b', letterSpacing: '1px', textTransform: 'uppercase' }}>
                      <i className="bi bi-bank2 me-1"></i>Revolving Facility Capacity
                    </span>
                    <div className="d-flex gap-3">
                      <span style={{ fontSize: '0.75rem', color: '#e2e8f0' }}>Limit: <strong>$755,000,000</strong></span>
                      <span style={{ fontSize: '0.75rem', color: '#10b981' }}>Available: <strong>$755,000,000</strong></span>
                    </div>
                  </div>
                  <div className="progress" style={{ height: '6px', background: '#0f172a' }}>
                    <div className="progress-bar bg-success" style={{ width: '100%' }}></div>
                  </div>
                  <div className="mt-2 d-flex gap-3 flex-wrap">
                    <small style={{ color: '#64748b', fontSize: '0.7rem' }}>
                      <i className="bi bi-person-badge me-1"></i>Collateral Provider: Regulated Financial Institution
                    </small>
                    <small style={{ color: '#64748b', fontSize: '0.7rem' }}>
                      <i className="bi bi-person me-1"></i>Collateral Receiver: Drawing Institution
                    </small>
                    <small style={{ color: '#64748b', fontSize: '0.7rem' }}>
                      <i className="bi bi-safe me-1"></i>BitGo MPC: Custodian (tri-party)
                    </small>
                  </div>
                </div>

                <div className="d-flex flex-column gap-3">
                  {repoWorkflows.map((wf, idx) => {
                    const statusColor =
                      wf.status === 'SETTLED' ? '#a78bfa' :
                      wf.status === 'RELEASED' ? '#38bdf8' :
                      wf.status === 'CREATED' ? '#10b981' :
                      wf.status === 'OPEN' ? '#10b981' :
                      wf.status === 'AWAITING_PAYMENT' ? '#f59e0b' :
                      wf.status === 'ACTIVE' ? '#10b981' :
                      wf.status === 'PENDING' ? '#64748b' : '#64748b';
                    return (
                      <div key={idx} className="p-3 rounded-3 border d-flex align-items-center gap-3"
                        style={{ borderColor: `${statusColor}44`, background: `${statusColor}06` }}>
                        <div className="p-2 rounded-3 flex-shrink-0"
                          style={{ background: `${statusColor}18` }}>
                          <i className={`bi ${wf.icon}`} style={{ fontSize: '1.3rem', color: statusColor }}></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h6 className="mb-0 fw-bold text-white" style={{ fontSize: '0.88rem' }}>{wf.stage}</h6>
                            <span className="badge" style={{ background: `${statusColor}18`, color: statusColor, fontSize: '0.6rem' }}>
                              {wf.status}
                            </span>
                            <span style={{ fontSize: '0.65rem', color: '#475569' }}>{wf.phase}</span>
                          </div>
                          <p className="mb-0 mt-1" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{wf.detail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Architecture note */}
                <div className="mt-4 p-3 rounded-3" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderLeft: '3px solid #6366f1' }}>
                  <div className="d-flex gap-2 align-items-start">
                    <i className="bi bi-info-circle-fill text-primary mt-1 flex-shrink-0"></i>
                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: 1.6 }}>
                      <strong className="text-white">K-R-O is not the lender, custodian, or clearing agent.</strong>
                      {' '}K-R-O is the bridge — the gateway — that presents terms in a standardized, Canton-native format.
                      The <code className="text-light">kroOS</code> party is <strong>observer only</strong>.
                      All execution authority lies with the Collateral Provider, Collateral Receiver, BitGo MPC, and the Clearing Agent.
                      <br /><br />
                      <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
                        Daml module: <code>Repo.Intraday</code> ·
                        Spec: <code>is-intraday-repo-flow.md</code> ·
                        Workspace: <code>contracts/daml/kro-settlement/daml/Repo/Intraday.daml</code>
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ── LIVE CANTON LEDGER TAB ── */}
            {cantonTab === 'ledger' && (
              <div>
                {/* Network banner */}
                <div className="p-3 rounded-3 mb-3 d-flex align-items-center gap-3"
                  style={{ background: `${networkBadgeColor}0d`, border: `1px solid ${networkBadgeColor}33` }}>
                  <i className="bi bi-hdd-network-fill" style={{ fontSize: '1.5rem', color: networkBadgeColor }}></i>
                  <div className="flex-grow-1">
                    <div style={{ fontSize: '0.7rem', color: networkBadgeColor, fontWeight: '800', letterSpacing: '1px' }}>
                      CANTON NETWORK — {activeNetwork.toUpperCase()} LIVE STATE
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.2rem' }}>
                      {cantonNetworkStatus.loading
                        ? 'Loading network state…'
                        : `Package: kro-settlement v${cantonNetworkStatus.packageVersion || '0.1.3'} · Party: ${(cantonNetworkStatus.partyId || '').substring(0, 28)}… · Last TX: ${cantonNetworkStatus.lastEventAt ? new Date(cantonNetworkStatus.lastEventAt).toLocaleString() : '—'}`
                      }
                    </div>
                  </div>
                  <span className="badge" style={{ background: `${networkBadgeColor}22`, color: networkBadgeColor, fontSize: '0.65rem', padding: '0.3rem 0.7rem' }}>
                    {networkPillLabel}
                  </span>
                </div>

                {cantonLedger.loading && (
                  <div className="text-center py-4 text-secondary">
                    <span className="efcp-spin me-2">⛓</span>Loading Canton ledger data…
                  </div>
                )}

                {/* Summary metrics */}
                {cantonLedger.summary && (
                  <div className="row g-2 mb-3">
                    {[
                      { label: 'Total Events', value: cantonLedger.summary.totalEvents, color: '#818cf8' },
                      { label: 'Active Contracts', value: cantonLedger.summary.activeContracts, color: '#10b981' },
                      { label: 'MainNet Records', value: cantonLedger.summary.mainnetRecords, color: '#34d399' },
                      { label: 'Total Notional', value: `$${(cantonLedger.summary.totalNotionalUsd / 1e6).toFixed(1)}M`, color: '#fbbf24' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="col-6 col-md-3">
                        <div className="p-2 rounded-3 text-center" style={{ background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(51,65,85,0.5)' }}>
                          <div style={{ fontSize: '0.6rem', color: '#64748b', letterSpacing: '0.8px', textTransform: 'uppercase' }}>{label}</div>
                          <div style={{ fontSize: '1.2rem', fontWeight: '800', color }}>{value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty state */}
                {!cantonLedger.loading && cantonLedger.loaded && cantonLedger.contracts.length === 0 && (
                  <div className="text-center py-4" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                    No Canton records found. Run <code>node backend/scripts/canton-feed.js mainnet</code> to populate.
                  </div>
                )}

                {/* Contract rows */}
                <div className="d-flex flex-column gap-2">
                  {cantonLedger.contracts.map((rec) => {
                    const meta = (() => { try { return JSON.parse(rec.metadata || '{}'); } catch { return {}; } })();
                    const isActive = rec.event_type?.includes('ACTIVE') || rec.event_type?.includes('HERITAGE');
                    const templateShort = rec.template_id?.split(':').pop() || rec.event_type;
                    const statusColor = isActive ? '#10b981' : '#475569';
                    return (
                      <div key={rec.id} className="p-3 rounded-3 d-flex align-items-center gap-3"
                        style={{ background: isActive ? 'rgba(16,185,129,0.04)' : 'rgba(255,255,255,0.02)', border: `1px solid ${statusColor}33` }}>
                        <div className="flex-shrink-0 p-2 rounded-3" style={{ background: `${statusColor}18` }}>
                          <i className={`bi ${isActive ? 'bi-check-circle-fill' : 'bi-archive'}`}
                            style={{ fontSize: '1.2rem', color: statusColor }}></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>{templateShort}</span>
                            <span className="badge" style={{ background: `${statusColor}18`, color: statusColor, fontSize: '0.6rem' }}>
                              {isActive ? 'ACTIVE' : 'ARCHIVED'}
                            </span>
                            {rec.uetr && <span style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'monospace' }}>{rec.uetr}</span>}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.2rem' }}>
                            {rec.event_type}
                            {meta.closeoutReceiptCid && <span className="ms-2">· CloseOut: {meta.closeoutReceiptCid}</span>}
                            {rec.event_timestamp && <span className="ms-2">· {rec.event_timestamp.slice(0, 19).replace('T', ' ')} UTC</span>}
                          </div>
                        </div>
                        {rec.amount_usd && (
                          <div className="text-end flex-shrink-0">
                            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#fbbf24' }}>
                              ${(parseFloat(rec.amount_usd) / 1e6).toFixed(3)}M
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 p-2 rounded-2" style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(51,65,85,0.3)' }}>
                  <small style={{ color: '#475569', fontSize: '0.65rem' }}>
                    <i className="bi bi-database me-1"></i>
                    Source: Azure SQL · canton_event_log · KRO API /api/v1/canton/contracts
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DAML Smart Contracts View */}
      {activeView === 'daml' && (
        <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b' }}>
          <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #0f172a, #1a2332)' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <i className="bi bi-file-earmark-code-fill fs-4 text-info me-2"></i>
                <div>
                  <h5 className="mb-0 fw-bold text-white">DAML Smart Contract Flow</h5>
                  <small className="text-white opacity-75">Canton-native Daml contract lifecycle — deterministic settlement logic</small>
                </div>
              </div>
              <span className="badge bg-info bg-opacity-25 text-info fw-bold">DAML 2.x + CANTON</span>
            </div>
          </div>
          <div className="card-body p-4">
            {/* Contract Architecture Overview */}
            <div className="p-4 rounded-3 mb-4" style={{ background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.15)' }}>
              <h6 className="fw-bold mb-3 text-white" style={{ fontSize: '0.85rem' }}>
                <i className="bi bi-diagram-3 me-2 text-info"></i>Contract Architecture — Canton DvP + Intraday Repo
              </h6>
              <p style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.7 }}>
                All settlement logic is encoded in Daml 2.x smart contracts deployed on the Canton Network (privacy-enabled, deterministic finality).
                Contracts enforce atomic Delivery-versus-Payment (DvP) — both collateral and cash legs settle simultaneously or both roll back, eliminating principal risk by design.
                Multi-party authorization follows the Daml signatory/observer model — no single party can unilaterally execute.
                Canton's sub-transaction privacy ensures only parties to a specific contract see its payload (unlike public blockchains).
              </p>
              <div className="row g-2 mt-2">
                {[
                  { label: 'Finality Model', value: 'Deterministic T+0 — not probabilistic · 24/7 settlement-ready' },
                  { label: 'Privacy', value: 'Sub-transaction privacy — trade details visible only to counterparties' },
                  { label: 'DvP Standard', value: 'Atomic DvP — both legs settle or both roll back · via Canton Global Synchronizer' },
                  { label: 'Token Standard', value: 'CIP-0056 / CIP-0112 — allocation-based settlement with net locking' },
                  { label: 'Interoperability', value: 'Cross-synchronizer reassignment — assets move between home sync and Global Sync' },
                  { label: 'Legal Framework', value: 'UCC Article 12 + Hague Securities Convention' },
                ].map(({ label, value }) => (
                  <div key={label} className="col-md-6">
                    <div className="p-2 rounded" style={{ background: 'rgba(56,189,248,0.04)', fontSize: '0.72rem' }}>
                      <span style={{ color: '#475569' }}>{label}: </span>
                      <span style={{ color: '#e2e8f0' }}>{value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contract Modules */}
            <h6 className="fw-bold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              Daml Contract Modules
            </h6>
            <div className="row g-3 mb-4">
              {[
                { module: 'Settlement.DvP', status: 'Active', desc: 'Atomic DvP — collateral leg (Provenance marker pledge) and cash leg (on-chain CIP-56 cash token) settle simultaneously via Global Synchronizer or both roll back. Daml contract requires both signatories (Provider + Receiver) to authorize. Applies Canton Foundation open-source DvP standards.', icon: 'bi-arrow-left-right', color: '#38bdf8' },
                { module: 'Allocation.CIP0056', status: 'Active', desc: 'Uses CIP-0056/CIP-0112 token standard patterns. AllocationFactory_Allocate locks holdings into Allocation contracts with allocateBefore deadline. Executor (settlement agent) controls lifecycle via Allocation_ExecuteTransfer (settle) or Allocation_Cancel (timeout). Net amounts locked per CIP-0112 — reduces capital drag. Supports multi-leg settlements up to 25 transfer legs.', icon: 'bi-lock-fill', color: '#60a5fa' },
                { module: 'Marker.Lifecycle', status: 'Active', desc: 'On-chain marker lifecycle: Finalize → Activate → Grant → Mint → Transfer → Withdraw → Burn. Each operation writes a Provenance TX hash and block height. DTIF (ISO 24165) registry ID stamped at creation. Webhook events fire to Azure on every state transition.', icon: 'bi-tag-fill', color: '#10b981' },
                { module: 'Escrow.Governor', status: 'Active', desc: 'Escrow-governed holding pattern — markers locked until all conditions met (collateral adequacy, BitGo MPC approval, clearing agent approval). Models traditional tri-party repo where collateral is held by an independent custodian. Release requires exercise of Canton choice by authorized signatory.', icon: 'bi-safe-fill', color: '#f59e0b' },
                { module: 'Margin.Automation', status: damlModuleStatus, desc: 'Continuous intraday margin monitoring. Oracle price feed triggers revaluation every 15 minutes. MarginCall Daml choice fires automatically when mark-to-market exposure exceeds maintenance threshold. Variation margin settled atomically on Canton — no manual reconciliation. Applies Canton Foundation collateral management standards. Nonconsuming choice — repo stays active during margin call.', icon: 'bi-graph-up-arrow', color: '#f59e0b' },
                { module: 'Collateral.Substitution', status: damlModuleStatus, desc: 'SubstituteCollateral choice: swap pledged collateral with equivalent eligible assets without full close-out/reopen. Atomic single-transaction swap — original released, replacement locked. Requires custodian re-verification (BitGo MPC). Eligible asset type + haircut adequacy + concentration limits enforced. Standard tri-party repo feature per ICMA best practices.', icon: 'bi-arrow-left-right', color: '#818cf8' },
                { module: 'Receipt.Finality', status: 'Active', desc: 'CloseOutReceipt: immutable post-settlement record. Aggregates: CloseOut Receipt CID, UETR, settlementTxHash, Canton block height, DTIF, Document Key ref. AES-256-GCM encryption for bank counterparty PDF. Models the on-ledger settlement confirmation.', icon: 'bi-patch-check-fill', color: '#a78bfa' },
                { module: 'Compliance.Gate', status: 'Active', desc: 'Pre-settlement compliance gate: KYC/AML attestation verification (GLEIF LEI + KRO Encrypted Virtual ID, modeled on vLEI best practices), OFAC/EU/UN sanctions screening. Blocks settlement if any check fails. Nonconsuming choice — contract stays active for retry.', icon: 'bi-shield-lock-fill', color: '#f472b6' },
                { module: 'Repo.Intraday', status: damlModuleStatus, desc: 'Revolving intraday repo facility ($910M accountability markers, $10M per draw, 2% initial margin). Modeled on FICC/DTCC GCF repo workflows in Daml — KRO does not operate as a CCP or FICC member. CIP-0056 allocation locking: IntradayRepoFacility → Allocate → CustodianApprove → ClearingAgentApprove → GlobalSync DvP → MarginMonitor → CollateralSub → ReturnFunds → CloseOut. Capacity regenerates intraday. Repo interest: notional × quoted rate × (ACT/360). Pilot: $10M cap/txn, 2/day, 10 days, accountability credits only. 24/7 settlement-ready.', icon: 'bi-bank2', color: '#818cf8' },
              ].map(({ module, status, desc, icon, color }) => (
                <div key={module} className="col-md-6">
                  <div className="p-3 rounded-3 h-100" style={{ background: `${color}06`, border: `1px solid ${color}22` }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className={`bi ${icon}`} style={{ color, fontSize: '1.1rem' }}></i>
                      <code className="fw-bold text-light" style={{ fontSize: '0.82rem' }}>{module}</code>
                      <span className="badge ms-auto" style={{ background: `${color}18`, color, fontSize: '0.6rem' }}>{status}</span>
                    </div>
                    <p className="mb-0" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contract Lifecycle Flow — Intraday Repo Draw */}
            <h6 className="fw-bold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              Intraday Repo Credit Draw Lifecycle (Daml)
            </h6>
            <div className="d-flex flex-column gap-2 mb-4" style={{ maxWidth: '700px', margin: '0 auto' }}>
              {[
                { step: '1', label: 'Collateral Pledge', desc: 'Collateral Provider posts eligible securities. Daml pre-condition enforces: collateral*(1−haircut) ≥ notional. Collateral Matrix defines eligible asset types, concentration limits, and haircut schedule. KRO retains full ownership — assets remain in custody vault. Counterparty receives qualified purchaser status with security interest via pledge escrow. No re-hypothecation permitted.', color: '#38bdf8' },
                { step: '2', label: 'CustodianApproveCollateral', desc: 'BitGo MPC (tri-party custodian) revalues collateral at real-time oracle price. Haircut re-applied on verified NAV. Concentration limit: single-issuer ≤ 25%. Daml choice exercised by custodian party — nonconsuming, draw stays active.', color: '#10b981' },
                { step: '3', label: 'ClearingAgentApprove', desc: 'Clearing gate modeled on FICC/DTCC GCF repo workflows — KRO does not operate as a CCP or FICC member. AML/KYC re-verified, sanctions screening. Nonconsuming choice — draw contract remains active for DvP. Clearing Gate: PASSED.', color: '#f472b6' },
                { step: '4', label: 'ReleaseFunds → Atomic DvP', desc: 'Collateral Provider exercises ReleaseFunds. Canton DvP: collateral leg (marker transfer on Provenance) ↔ cash leg (on-chain CIP-56 cash token). Both legs atomic — if either fails, full rollback. usedCapacity += notional. UETR embedded in the on-chain cash transfer.', color: '#f59e0b' },
                { step: '5', label: 'ReturnFunds → CloseOutReceipt', desc: 'Collateral Receiver returns funds intraday. usedCapacity decremented → facility capacity regenerated (revolving). CloseOutReceipt: SETTLED. On-chain anchors: DTIF + CloseOut Receipt CID + settlementTxHash + Canton block height. Document Key (AES-256) issued. UCC Art. 12 perfection confirmed.', color: '#a78bfa' },
              ].map((item, i, arr) => (
                <React.Fragment key={item.step}>
                  <div className="p-3 rounded border text-start w-100" style={{ background: `${item.color}06`, borderColor: `${item.color}22` }}>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold" style={{ color: item.color }}>{item.step}.</span>
                      <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>{item.label}</span>
                    </div>
                    <p className="mb-0 ms-4 mt-1" style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{item.desc}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="text-center"><i className="bi bi-arrow-down" style={{ color: '#475569' }}></i></div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Daml Contract Source Reference */}
            <h6 className="fw-bold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              Daml Contract Source — Reference Templates
            </h6>
            {[
              { name: 'Repo.Intraday', file: 'Intraday.daml', code: `module Repo.Intraday where

-- KRO (requestor / cash buyer) presents a standard RFQ (request-for-quote)
-- service request for price discovery. The approved settlement provider
-- (respondent) is the AcceptRFP controller — it reviews, quotes the intraday
-- rate, and accepts. Standard account/service trigger, not a partnership.

template IntradayRepoRFP
  with
    borrower        : Party    -- KRO, steward of private-wealth clients (RFQ requestor)
    borrowerLEI     : Text
    approvedLenders : [Party]  -- approved settlement providers (Canton-validated)
    drawNotional    : Decimal
    haircutPercent  : Decimal
    intradayRateBps : Decimal
    collateralRef   : Text     -- denom / scope hash of pledged collateral
    cashLegRef      : Text     -- on-chain CIP-56 cash token reference
    dtifId          : Text     -- ISO-24165 transaction identifier
    startTime       : Time
    expiryTime      : Time     -- RFQ auto-expires; instrument left to provider classification
    kroOS           : Party    -- observer only — no execution authority
  where
    ensure drawNotional > 0.0 && expiryTime > startTime
        && haircutPercent >= 0.0 && haircutPercent < 1.0
        && intradayRateBps > 0.0
    signatory borrower
    observer  approvedLenders, kroOS

    -- FCFS: first approved provider to accept wins. Provider is the controller.
    choice AcceptRFP : RFPAcceptanceResult
      with
        lender          : Party
        lenderLEI       : Text
        custodian       : Party
        clearingAgent   : Party
        capacityLimit   : Decimal
        collateralValue : Decimal   -- provider's standard classification of value
        maturityTime    : Time
      controller lender
      do
        assertMsg "Lender must be an approved settlement provider"
          (lender \`elem\` approvedLenders)
        now <- getTime
        assertMsg "RFP has expired" (now <= expiryTime)
        assertMsg "Adjusted collateral insufficient"
          ((collateralValue * (1.0 - haircutPercent)) >= drawNotional)
        -- Atomically creates facility + open draw + heritage record
        facilityCid <- create IntradayRepoFacility with ..
        drawCid     <- exercise facilityCid OpenDraw with ..
        heritageCid <- create HeritageNetworkRecord with ..
        return RFPAcceptanceResult with facilityCid; drawCid; heritageCid

    choice CancelRFP : ()        -- borrower withdraws before acceptance
      controller borrower
      do return ()

    choice ExpireRFP : ()        -- on-chain cleanup after expiry
      controller borrower
      do
        now <- getTime
        assertMsg "RFP not yet expired" (now > expiryTime)
        return ()` },
              { name: 'Settlement.DvP', file: 'AtomicDvP.daml', code: `module Settlement.DvP where

import Daml.Finance.Interface.Allocation qualified as Allocation

template IntradayRepoDraw
  with
    collateralProvider : Party
    collateralReceiver : Party
    custodian          : Party
    kroOS              : Party
    notional           : Decimal
    repoRate           : Decimal
    dayCount           : Text
    collateralDenom    : Text
    haircutPct         : Decimal
    facilityLimit      : Decimal
    usedCapacity       : Decimal
    status             : Text
  where
    signatory collateralProvider, collateralReceiver
    observer custodian, kroOS

    nonconsuming choice CustodianApproveCollateral : ()
      with
        verifiedNAV   : Decimal
        oraclePrice   : Decimal
        oracleTime    : Time
      controller custodian
      do
        let postHaircut = verifiedNAV * (1.0 - haircutPct)
        assertMsg "Collateral inadequate after haircut"
          (postHaircut >= notional)

    nonconsuming choice ClearingAgentApprove : ()
      with
        kycPassed       : Bool
        sanctionsClear  : Bool
      controller custodian
      do
        assertMsg "KYC not passed" kycPassed
        assertMsg "Sanctions not clear" sanctionsClear

    choice ExecuteDvP : ContractId CloseOutReceipt
      with
        cashLegRef      : Text
        uetr            : Text
        provenanceTxHash: Text
        blockHeight     : Int
      controller collateralProvider, collateralReceiver
      do
        assertMsg "Draw not ready for DvP"
          (status == "AWAITING_ALLOCATION"
           || status == "GATES_PASSED")
        create CloseOutReceipt with
          settlementStatus = "SETTLED"
          ..

    choice ReturnFunds : ContractId IntradayRepoFacility
      controller collateralReceiver
      do
        create IntradayRepoFacility with
          usedCapacity = usedCapacity - notional
          ..

template CloseOutReceipt
  with
    collateralProvider  : Party
    collateralReceiver  : Party
    custodian           : Party
    kroOS               : Party
    notional            : Decimal
    cashLegRef          : Text
    uetr                : Text
    provenanceTxHash    : Text
    blockHeight         : Int
    settlementStatus    : Text
    collateralDenom     : Text
  where
    signatory collateralProvider, collateralReceiver
    observer custodian, kroOS` },
              { name: 'Margin.Automation', file: 'MarginMonitor.daml', code: `module Margin.Automation where

template MarginMonitor
  with
    collateralProvider : Party
    collateralReceiver : Party
    custodian          : Party
    drawCid            : ContractId IntradayRepoDraw
    maintenanceThreshold : Decimal
    revalIntervalMins  : Int       -- 15
  where
    signatory custodian
    observer collateralProvider, collateralReceiver

    nonconsuming choice RevalueCollateral : Optional (ContractId MarginCall)
      with
        currentNAV    : Decimal
        oraclePrice   : Decimal
        notional      : Decimal
        haircutPct    : Decimal
      controller custodian
      do
        let exposure = notional - (currentNAV * (1.0 - haircutPct))
        if exposure > maintenanceThreshold
          then do
            cid <- create MarginCall with
              requiredAmount = exposure
              ..
            pure (Some cid)
          else pure None

template MarginCall
  with
    collateralProvider : Party
    collateralReceiver : Party
    custodian          : Party
    requiredAmount     : Decimal
  where
    signatory custodian
    observer collateralProvider, collateralReceiver

    choice PostAdditionalCollateral : ()
      controller collateralProvider
      do pure ()

    choice SubstituteCollateral : ()
      with
        newDenom : Text
        newQty   : Decimal
      controller collateralProvider
      do pure ()` },
              { name: 'Collateral.Offer', file: 'CollateralOffer.daml', code: `module Collateral.Offer where

-- kro-credit-protocol-v2 · v0.1.4 · offer/take instrument
-- Data standard carried Offer -> Position -> CloseOutReceipt.

data SettlementSpec = SettlementSpec with
    settlementMethod : Text   
    cashAccountRef   : Text   
    currency         : Text   
    instructionRef   : Text   

-- COLLATERAL OFFER (v0.1.4) -- deterministic settlement primitive.
-- The offeror presents clean, unencumbered collateral; an eligible taker
-- accepts or the offer lapses. First valid acceptance wins (FCFS, ledger-enforced).
template CollateralOffer with
    offeror              : Party              -- holder of record (KRO Brokers, SPC)
    eligibleTakers       : [Party]            -- enumerated set of permitted takers
    collateralId         : ContractId MarkerCollateral
    offeredAmountUsd     : Decimal
    commercialValueUsd   : Decimal            -- real-world commercial value
    unencumbered         : Bool               -- attestation: clean title, unpledged
    eligibilityProfileId : Text               -- compliance ruleset reference
    settlementSpec       : SettlementSpec     -- cash settlement instructions
    offerRef             : Text               -- UETR / offer identifier
    initialMargin        : Decimal            -- e.g., 0.02 for 2% initial margin (haircut)
    pricingBps           : Decimal            -- e.g., 10.0 for 10 bps intraday
    positionExpiry       : Time               -- maturity timestamp
  where
    ensure offeredAmountUsd > 0.0
        && commercialValueUsd > 0.0
        && unencumbered
        && eligibilityProfileId /= ""
        && offerRef /= ""
        && not (null eligibleTakers)
        && initialMargin >= 0.0 && initialMargin < 1.0
        && pricingBps >= 0.0
    signatory offeror
    observer  eligibleTakers

    choice AcceptCollateral : ContractId CollateralPosition
      with taker : Party
      controller taker
      do
        assertMsg "Taker is not eligible" (taker \`elem\` eligibleTakers)
        collateral <- fetch collateralId
        assertMsg "Collateral not held by offeror"
          (collateral.kro == offeror)
        -- Margin enforcement: offered amount <= (1 - margin) * collateral value
        assertMsg "Offered amount exceeds collateral value with margin"
          (offeredAmountUsd <= (1.0 - initialMargin) * intToDecimal collateral.units * collateral.priceUsd)
        lockedCid <- exercise collateralId LockForPledge with pledgeUetr = offerRef
        create CollateralPosition with
          offeror; taker; lockedCollateralId = lockedCid
          positionAmountUsd = offeredAmountUsd; commercialValueUsd
          eligibilityProfileId; settlementSpec; offerRef
          pricingBps; positionExpiry

    choice WithdrawOffer : ()
      controller offeror
      do pure ()

-- Acceptance creates position; cash leg authorized.
template CollateralPosition with
    offeror              : Party
    taker                : Party
    lockedCollateralId   : ContractId LockedMarkerCollateral
    positionAmountUsd    : Decimal
    commercialValueUsd   : Decimal
    eligibilityProfileId : Text
    settlementSpec       : SettlementSpec
    offerRef             : Text
    pricingBps           : Decimal
    positionExpiry       : Time
  where
    ensure positionAmountUsd > 0.0
    signatory offeror, taker

    choice SettleCollateral : (ContractId CloseOutReceipt, ContractId MarkerCollateral)
      with instructionRef : Text; settlementTxHash : Text
      controller offeror
      do
        now         <- getTime
        cashSettled <- verifyCashLeg positionAmountUsd settlementTxHash
        assertMsg "Cash leg not settled" cashSettled
        collCid    <- exercise lockedCollateralId UnlockCollateral
        receiptCid <- create CloseOutReceipt with
          kro = offeror; counterparty = taker
          settledAmountUsd = positionAmountUsd; uetr = offerRef
          instructionRef; settlementTxHash; settledAt = now
          eligibilityProfileId = Some eligibilityProfileId
          settlementSpec       = Some settlementSpec
          pricingBps           = Some pricingBps
        return (receiptCid, collCid)

    -- Unwind the position and reclaim clean collateral after maturity.
    choice ReturnCollateral : ContractId MarkerCollateral
      controller offeror
      do
        now <- getTime
        assertMsg "Position not yet expired" (now > positionExpiry)
        exercise lockedCollateralId UnlockCollateral` },
            ].map(({ name, file, code }) => (
              <div key={name} className="card border-0 mb-3" style={{ background: '#0f172a', border: '1px solid #1e293b' }}>
                <div className="card-header d-flex align-items-center justify-content-between py-2" style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}>
                  <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-file-earmark-code text-info"></i>
                    <code className="text-info fw-bold" style={{ fontSize: '0.82rem' }}>{name}</code>
                    <small className="text-muted">— {file}</small>
                  </div>
                  <button className="btn btn-sm btn-outline-info" style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem' }}
                    onClick={() => navigator.clipboard.writeText(code)}>
                    <i className="bi bi-clipboard me-1"></i>Copy
                  </button>
                </div>
                <div className="card-body p-0">
                  <pre className="mb-0 p-3" style={{ background: '#0f172a', color: '#e2e8f0', fontSize: '0.72rem', lineHeight: 1.5, overflowX: 'auto', maxHeight: '400px', whiteSpace: 'pre' }}>{code}</pre>
                </div>
              </div>
            ))}

            {/* Technical References */}
            <div className="p-3 rounded-3 mt-3" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderLeft: '3px solid #6366f1' }}>
              <div className="d-flex gap-2 align-items-start">
                <i className="bi bi-info-circle-fill text-primary mt-1 flex-shrink-0"></i>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>
                  <strong className="text-white">Deployment Reference</strong>
                  <br />
                  Daml contracts are deployed via the Canton participant node <code className="text-light">KRO-Mainnet-1</code> operated by Infra Singularity (IS).
                  Templates authored in Daml 2.x, compiled to Canton-native Daml-LF packages.
                  KRO publishes <code className="text-light">IntradayRepoRFP</code> (a price-discovery RFQ) — the counterparty (respondent) reviews, quotes the intraday rate, and exercises <code className="text-light">AcceptRFP</code>; otherwise KRO exercises <code className="text-light">CancelRFP</code> or the RFQ auto-expires via <code className="text-light">ExpireRFP</code>. Instrument left open for the counterparty's standard classification.
                  Settlement is fully on-ledger — collateral pledge, CIP-56 allocation lock, atomic DvP execution, and CloseOutReceipt finality all occur as Canton contract lifecycle events.
                  <br /><br />
                  <span style={{ color: '#64748b', fontSize: '0.72rem' }}>
                    Workspace: <code className="text-light">contracts/daml/kro-settlement/</code> ·
                    Runtime: <code className="text-light">Canton 2.x + Daml 2.x</code> ·
                    Node: <code className="text-light">KRO-Mainnet-1 (IS Validator)</code> ·
                    Standards: <code className="text-light">Daml protocol-enforced settlement · CIP-0056/CIP-0112 · UCC Art. 12 · ISO 24165 (DTIF)</code>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Key View */}
      {activeView === 'document-key' && (
        <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b' }}>
          <div className="card-header border-bottom" style={{ background: 'linear-gradient(135deg, #312e81, #4338ca)' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <i className="bi bi-key-fill fs-4 text-warning me-2"></i>
                <div>
                  <h5 className="mb-0 fw-bold text-white">Document Key — AES-256 Settlement Receipt</h5>
                  <small className="text-white opacity-75">Post-settlement encrypted evidentiary package for bank counterparty</small>
                </div>
              </div>
              <span className="badge bg-success px-2 py-1" style={{ fontSize: '0.65rem' }}>
                <i className="bi bi-circle-fill me-1" style={{ fontSize: '0.35rem', verticalAlign: 'middle' }}></i>DOCUMENT VAULT
              </span>
            </div>
          </div>
          <div className="card-body p-4">

            {/* Key metadata */}
            <div className="row g-3 mb-4">
              {[
                { field: 'Encryption', value: 'AES-256-GCM', icon: 'bi-lock-fill', color: '#818cf8' },
                { field: 'Storage', value: 'kromarketplacestorage / marker-assignments-prod', icon: 'bi-cloud-fill', color: '#38bdf8' },
                { field: 'Access Control', value: 'Token-gated · Per-access audit log · Time-boxed URL', icon: 'bi-shield-lock-fill', color: '#10b981' },
                { field: 'Root Registry Key', value: 'RSID17738103:CHR2:DF98:17738103:DNAMARGUERITEOPHELIA', icon: 'bi-key-fill', color: '#f59e0b' },
              ].map(({ field, value, icon, color }, idx) => (
                <div key={idx} className="col-md-6">
                  <div className="p-3 rounded-3" style={{ background: `${color}08`, border: `1px solid ${color}22` }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <i className={`bi ${icon}`} style={{ color, fontSize: '0.9rem' }}></i>
                      <small style={{ color: '#64748b', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>{field}</small>
                    </div>
                    <div style={{ color: '#e2e8f0', fontSize: '0.82rem', fontFamily: field === 'Root Registry Key' ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* TradFi Document Checklist */}
            <h6 className="fw-bold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              <i className="bi bi-file-earmark-check me-1"></i>Settlement Document Package — Contents Confirmed
            </h6>
            <div className="table-responsive mb-4">
              <table className="table table-dark table-sm align-middle mb-0" style={{ fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155' }}>
                    <th style={{ color: '#94a3b8', width: 40 }}>#</th>
                    <th style={{ color: '#94a3b8' }}>Document</th>
                    <th style={{ color: '#94a3b8' }}>Standard / Authority</th>
                    <th style={{ color: '#94a3b8' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { doc: 'Marker Assignment Receipt', std: 'Provenance pio-mainnet-1 · ZoneScan verifiable TX hash', status: 'CONFIRMED' },
                    { doc: 'Escrow Settlement Confirmation', std: 'Canton DvP atomic settlement · CloseOutReceipt on-chain', status: 'CONFIRMED' },
                    { doc: 'CIP-56 Allocation & Transfer Record', std: 'Allocation → Allocation_ExecuteTransfer → CloseOutReceipt · Canton on-ledger', status: 'CONFIRMED' },
                    { doc: 'BitGo MPC Custody Authorization', std: 'SOC 2 Type II · MPC 2-of-3 threshold signature audit trail', status: 'CONFIRMED' },
                    { doc: 'FedWire Settlement Confirmation', std: 'Federal Reserve Regulation J · RTGS irrevocable finality', status: 'CONFIRMED' },
                    { doc: 'UCC Article 12 Perfection', std: 'UCC § 12-104 "Control" of Controllable Electronic Record', status: 'PERFECTED' },
                    { doc: 'DTIF Registry Entry', std: 'ISO 24165 · Digital Token Identifier Foundation', status: 'REGISTERED' },
                    { doc: 'Collateral Adequacy Report', std: 'Eligible collateral matrix + haircut schedule · Daml protocol-enforced', status: 'VALIDATED' },
                    { doc: 'KRO Encrypted Virtual ID Credential', std: 'GLEIF LEI (ISO 17442) · Encrypted Virtual ID modeled on vLEI/KERI best practices', status: 'ANCHORED' },
                    { doc: 'AML/KYC Compliance Gate Record', std: 'OFAC SDN + EU Consolidated + UN Security Council lists', status: 'CLEARED' },
                    { doc: 'Canton Finality Proof', std: 'Canton deterministic T+0 · Block height + contract ID + signatories', status: 'IMMUTABLE' },
                    { doc: 'Trust Index Entry', std: 'KRO Stewardship Intelligence · Institutional-grade evidentiary record', status: 'ANCHORED' },
                  ].map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #1e293b' }}>
                      <td style={{ color: '#475569' }}>{idx + 1}</td>
                      <td className="fw-bold" style={{ color: '#e2e8f0' }}>{row.doc}</td>
                      <td><code style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{row.std}</code></td>
                      <td>
                        <span className={`badge bg-${row.status === 'CONFIRMED' || row.status === 'IMMUTABLE' ? 'success' : row.status === 'PERFECTED' ? 'primary' : row.status === 'ARCHIVED' ? 'info' : row.status === 'CLEARED' ? 'success' : 'warning text-dark'}`} style={{ fontSize: '0.6rem' }}>
                          <i className="bi bi-check-circle-fill me-1"></i>{row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Where documents are stored */}
            <h6 className="fw-bold mb-3 mt-2" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              <i className="bi bi-cloud-arrow-up me-1"></i>Document Storage Locations
            </h6>
            <div className="row g-2 mb-4">
              {[
                { name: 'Attestation of Affidavit', location: 'Azure Container App API', path: 'ca-api-prod-wus2 → /api/v1/attestation/all', icon: 'bi-file-earmark-lock-fill', color: '#818cf8', link: `${KRO_API_BASE}/attestation/all` },
                { name: 'Marker Assignment Records', location: 'Azure Blob Storage', path: 'kromarketplacestorage / marker-assignments-prod', icon: 'bi-tag-fill', color: '#10b981', link: null },
                { name: 'Provenance On-Chain TX', location: 'Provenance pio-mainnet-1', path: 'ZoneScan block explorer — immutable', icon: 'bi-link-45deg', color: '#a78bfa', link: 'https://zonescan.io/provenance/assets/kro.as.accountability.v2' },
                { name: 'Canton Ledger Contracts', location: 'Canton KRO-Mainnet-1', path: 'Daml Ledger API — DrawWorkflow / SettlementWorkflow', icon: 'bi-hdd-network-fill', color: '#f59e0b', link: null },
                { name: 'BitGo MPC Audit Trail', location: 'BitGo Enterprise', path: 'Enterprise API — SOC 2 Type II audited', icon: 'bi-shield-lock-fill', color: '#f472b6', link: null },
              ].map((doc, idx) => (
                <div key={idx} className="col-md-6">
                  <div className="p-2 rounded" style={{ background: `${doc.color}06`, border: `1px solid ${doc.color}22`, fontSize: '0.78rem' }}>
                    <div className="d-flex align-items-start gap-2">
                      <i className={`bi ${doc.icon} mt-1 flex-shrink-0`} style={{ color: doc.color }}></i>
                      <div className="flex-grow-1 overflow-hidden">
                        <strong style={{ color: '#e2e8f0', fontSize: '0.78rem' }}>{doc.name}</strong>
                        <div style={{ color: '#64748b', fontSize: '0.68rem' }}>{doc.location}</div>
                        <code style={{ color: doc.color, fontSize: '0.62rem', wordBreak: 'break-all' }}>{doc.path}</code>
                      </div>
                      {doc.link && (
                        <a href={doc.link} target="_blank" rel="noopener noreferrer" className="btn btn-sm flex-shrink-0" style={{ fontSize: '0.6rem', color: doc.color, border: `1px solid ${doc.color}33`, padding: '2px 8px' }}>
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Completed Affidavit Preview */}
            <h6 className="fw-bold mb-3" style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
              <i className="bi bi-file-earmark-check me-1"></i>Completed Attestation of Affidavit — Preview
            </h6>
            <div className="p-3 rounded-3 mb-4" style={{ background: 'rgba(129,140,248,0.06)', border: '1px solid rgba(129,140,248,0.2)', fontFamily: 'monospace', fontSize: '0.72rem', color: '#c7d2fe', lineHeight: 1.8 }}>
              <div className="mb-2" style={{ color: '#818cf8', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', fontSize: '0.65rem' }}>ATTESTATION OF AUTHORITY — SOVEREIGN TREASURY DRAW</div>
              <div style={{ borderBottom: '1px solid rgba(129,140,248,0.15)', paddingBottom: 8, marginBottom: 8 }}>
                <span style={{ color: '#64748b' }}>Authority Type:</span> <span style={{ color: '#e2e8f0' }}>KRO Encrypted Virtual ID (modeled on vLEI OOR best practices)</span><br/>
                <span style={{ color: '#64748b' }}>LEI Standard:</span> <span style={{ color: '#e2e8f0' }}>ISO 17442 — KRO holds a GLEIF-registered Legal Entity Identifier</span><br/>
                <span style={{ color: '#64748b' }}>Credential Anchor:</span> <span style={{ color: '#e2e8f0' }}>Encrypted Virtual ID — SHA-256 digest, modeled on KERI/ACDC patterns</span><br/>
                <span style={{ color: '#64748b' }}>Signatory Role:</span> <span style={{ color: '#e2e8f0' }}>Special Situations Partner (SSP)</span><br/>
                <span style={{ color: '#64748b' }}>Executing Entity:</span> <span style={{ color: '#e2e8f0' }}>KRO Brokers, SPC</span><br/>
                <span style={{ color: '#64748b' }}>Settlement Type:</span> <span style={{ color: '#e2e8f0' }}>Sovereign Treasury Draw with Discretionary Social Purpose Allocation</span><br/>
                <span style={{ color: '#64748b' }}>Pilot Parameters:</span> <span style={{ color: '#e2e8f0' }}>$10M cap/txn · 2 txns/day · 10 days · kro.as.accountability.v2 only</span>
              </div>
              <div style={{ borderBottom: '1px solid rgba(129,140,248,0.15)', paddingBottom: 8, marginBottom: 8 }}>
                <span style={{ color: '#64748b' }}>Root Registry Key:</span> <span style={{ color: '#f59e0b' }}>RSID17738103:CHR2:DF98:17738103:DNAMARGUERITEOPHELIA</span><br/>
                <span style={{ color: '#64748b' }}>Witness Propagation:</span> <span style={{ color: '#e2e8f0' }}>3/3 acknowledged</span><br/>
                <span style={{ color: '#64748b' }}>Digest:</span> <span style={{ color: '#e2e8f0' }}>SHA-256 anchored to encrypted event log</span>
              </div>
              <div style={{ color: '#a78bfa', fontStyle: 'italic' }}>
                "Exercising Executive Signing Authority as SSP to facilitate a Sovereign Treasury Draw with Discretionary Social Purpose Allocation. Word is Bond."
              </div>
              <div className="mt-2" style={{ color: '#475569', fontSize: '0.62rem' }}>
                Status: <span className="badge bg-success" style={{ fontSize: '0.55rem' }}>SEALED</span> · Stored at: <code style={{ color: '#818cf8' }}>ca-api-prod-wus2 → /api/v1/attestation/all</code>
              </div>
            </div>

            {/* Document Key generation info */}
            <div className="p-3 rounded-3 mb-3" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)' }}>
              <p className="mb-0" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.7 }}>
                <i className="bi bi-info-circle-fill text-primary me-1"></i>
                The Document Key aggregates all 13 documents above into a single AES-256-GCM encrypted PDF.
                The access token is issued to the bank counterparty, time-boxed (24h expiry), and every access is logged with IP, timestamp, and user agent.
                The package is stored in Azure Blob Storage (<code className="text-light">marker-assignments-prod</code>) and linked to the Root Registry Key
                <code className="text-light ms-1">RSID17738103:CHR2:DF98:17738103:DNAMARGUERITEOPHELIA</code>.
              </p>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <a href={`${TTD_PORTAL_BASE}?page=portfolio`} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                <i className="bi bi-box-arrow-up-right me-1"></i>Generate Document Key in TTD Portal
              </a>
              <a href={`${KRO_API_BASE}/attestation/all`} target="_blank" rel="noopener noreferrer" className="btn btn-outline-light">
                <i className="bi bi-file-earmark-lock me-1"></i>View Attestation Records (API)
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Affidavit Data Feed View ── */}
      {activeView === 'affidavit' && (
        <div style={{ marginBottom: '1.5rem' }}>

          {/* Card header */}
          <div style={{ background: 'linear-gradient(135deg, #0c1015, #1a1206)', borderRadius: '12px 12px 0 0', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <i className="bi bi-file-earmark-check-fill" style={{ fontSize: '1.3rem', color: '#c9a84c' }}></i>
              <div>
                <div style={{ fontWeight: '800', color: '#e8d5a0', fontSize: '1rem' }}>Affidavit of Authority &amp; Attestation</div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(201,168,76,0.6)' }}>NOTARY PUBLIC AUTHORITY OF RECORD — Tokenized credential modeled on GLEIF vLEI / KERI best practices</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.65rem', padding: '0.2rem 0.6rem', borderRadius: '999px', background: affidavitLastSync ? 'rgba(16,185,129,0.15)' : 'rgba(100,116,139,0.15)', color: affidavitLastSync ? '#10b981' : '#64748b', border: `1px solid ${affidavitLastSync ? 'rgba(16,185,129,0.3)' : 'rgba(100,116,139,0.2)'}` }}>
                {affidavitLastSync ? `Synced ${new Date(affidavitLastSync).toLocaleTimeString()}` : 'Not yet synced'}
              </span>
              <button onClick={fetchAffidavit} style={{ fontSize: '0.7rem', padding: '0.25rem 0.65rem', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid #334155', borderRadius: '5px', cursor: 'pointer' }}>
                <i className="bi bi-arrow-repeat me-1"></i>Refresh
              </button>
            </div>
          </div>

          {/* Official affidavit document */}
          <div style={{ marginBottom: '1.75rem' }}>

            {/* Affidavit header / preamble */}
            <div style={{ background: 'linear-gradient(180deg, #0c1015 0%, #080d12 100%)', border: '2px solid #c9a84c', borderBottom: 'none', padding: '1.5rem', textAlign: 'center', position: 'relative' }}>
              {['topLeft','topRight','btmLeft','btmRight'].map(pos => (
                <span key={pos} style={{ position: 'absolute', fontSize: '0.8rem', color: 'rgba(201,168,76,0.4)', top: pos.startsWith('top') ? '6px' : 'auto', bottom: pos.startsWith('btm') ? '6px' : 'auto', left: pos.endsWith('Left') ? '10px' : 'auto', right: pos.endsWith('Right') ? '10px' : 'auto' }}>&#10022;</span>
              ))}
              <div style={{ fontSize: '0.6rem', fontWeight: '800', color: '#c9a84c', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                K-R-O Private Market · Authority of Record
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: '900', color: '#e8d5a0', letterSpacing: '1px', marginBottom: '0.2rem' }}>
                AFFIDAVIT OF AUTHORITY AND ATTESTATION
              </div>
              <div style={{ fontSize: '0.65rem', color: 'rgba(201,168,76,0.6)', fontStyle: 'italic', marginBottom: '0.75rem' }}>
                Tokenized Credential, GLEIF LEI (ISO 17442) · Modeled on vLEI/KERI Best Practices
              </div>
              <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.4), transparent)', margin: '0 2rem 0.75rem' }} />
              <p style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>
                KNOW ALL PERSONS BY THESE PRESENTS that the undersigned, acting in their capacity as{' '}
                <strong style={{ color: '#e8d5a0' }}>Special Situations Partner, Executive Signing Authority</strong>,
                duly identified under a GLEIF-registered Legal Entity Identifier (LEI) with an encrypted virtual identity
                modeled on the vLEI Ecosystem Governance Framework best practices, hereby affirms under oath the following
                attestation as a binding instrument of record for the K-R-O Private Market.
              </p>
            </div>

            {/* Clause I: Legal Entity Credential */}
            <div style={{ background: '#080d12', border: '1px solid rgba(201,168,76,0.3)', borderTop: '1px solid rgba(201,168,76,0.15)', padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.9rem' }}>
                <span style={{ fontSize: '0.58rem', fontWeight: '900', color: '#c9a84c', letterSpacing: '2px', whiteSpace: 'nowrap' }}>I.</span>
                <span style={{ fontSize: '0.62rem', fontWeight: '800', color: '#c9a84c', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Legal Entity Credential, GLEIF LEI + Encrypted Virtual ID</span>
              </div>
              <p style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.7, margin: '0 0 1rem', fontStyle: 'italic' }}>
                The LEI identifies the legal entity under ISO 17442 (KRO holds a GLEIF-registered LEI). KRO's Encrypted Virtual ID
                is modeled on vLEI/KERI best practices, binding the organization and signatory role through SHA-256 digest anchoring.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.9rem' }}>
                {[
                  { label: 'Legal Entity Identifier (LEI), ISO 17442', key: 'lei', hint: 'KRO holds a GLEIF-registered 20-char LEI' },
                  { label: 'KRO Encrypted Virtual ID (Org)', key: 'organizationVleiRoot', hint: 'Encrypted Virtual ID modeled on vLEI/KERI best practices' },
                  { label: 'KRO Virtual ID — Role Binding', key: 'signatoryOor', hint: 'Encrypted role credential binding signatory to authorized role' },
                  { label: 'Organizational Role Designation', key: 'signatoryRole', hint: 'e.g. SPECIAL_SITUATIONS_PARTNER_ESA' },
                ].map(({ label, key, hint }) => (
                  <div key={key}>
                    <label style={{ display: 'block', fontSize: '0.6rem', fontWeight: '700', color: '#c9a84c', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '0.3rem' }}>{label}</label>
                    <input value={affidavit[key] || ''} onChange={e => setAffidavit(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '0.65rem 0.8rem', background: 'rgba(15,23,42,0.9)', color: '#e8d5a0', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '5px', fontSize: '0.8rem', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    <div style={{ fontSize: '0.58rem', color: '#334155', marginTop: '0.25rem', fontStyle: 'italic' }}>{hint}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Clause II: Settlement Mandate */}
            <div style={{ background: '#080d12', border: '1px solid rgba(201,168,76,0.3)', borderTop: '1px dashed rgba(201,168,76,0.12)', padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.9rem' }}>
                <span style={{ fontSize: '0.58rem', fontWeight: '900', color: '#c9a84c', letterSpacing: '2px' }}>II.</span>
                <span style={{ fontSize: '0.62rem', fontWeight: '800', color: '#c9a84c', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Settlement Mandate &amp; Execution Parameters</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.9rem' }}>
                {[
                  { label: 'Executing Entity', key: 'executingEntity', hint: 'Legal name + LEI of executing party' },
                  { label: 'Executing Wallet (AID / Address)', key: 'executingWallet', hint: 'Provenance pb1... or EVM 0x... address' },
                  { label: 'Funding Source, UCC Reference', key: 'fundingSource', hint: 'UCC-3 filing number or receivable ID' },
                  { label: 'Settlement Classification', key: 'settlementType', hint: 'e.g. RECEIVABLE_BACKED_TREASURY_DRAW' },
                  { label: 'Execution Priority Rail', key: 'priority', hint: 'e.g. EXTREME_FLASHBOTS_RPC' },
                  { label: 'Destination Address', key: 'destination', hint: 'EVM beneficiary wallet for USDT proceeds' },
                ].map(({ label, key, hint }) => (
                  <div key={key}>
                    <label style={{ display: 'block', fontSize: '0.6rem', fontWeight: '700', color: '#c9a84c', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '0.3rem' }}>{label}</label>
                    <input value={affidavit[key] || ''} onChange={e => setAffidavit(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '0.65rem 0.8rem', background: 'rgba(15,23,42,0.9)', color: '#e8d5a0', border: '1px solid rgba(201,168,76,0.25)', borderRadius: '5px', fontSize: '0.8rem', fontFamily: 'monospace', boxSizing: 'border-box' }} />
                    <div style={{ fontSize: '0.58rem', color: '#334155', marginTop: '0.25rem', fontStyle: 'italic' }}>{hint}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Clause III: Oath / Attestation Body */}
            <div style={{ background: '#080d12', border: '1px solid rgba(201,168,76,0.3)', borderTop: '1px dashed rgba(201,168,76,0.12)', padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.9rem' }}>
                <span style={{ fontSize: '0.58rem', fontWeight: '900', color: '#c9a84c', letterSpacing: '2px' }}>III.</span>
                <span style={{ fontSize: '0.62rem', fontWeight: '800', color: '#c9a84c', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Oath &amp; Attestation Body</span>
              </div>
              <p style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.7, margin: '0 0 0.75rem', fontStyle: 'italic' }}>
                The statement below constitutes the operative attestation clause. It shall be cryptographically
                hashed and anchored to KRO's encrypted event log (modeled on KERI/ACDC best practices) upon submission.
              </p>
              <textarea
                value={affidavit.attestation || ''}
                onChange={e => setAffidavit(prev => ({ ...prev, attestation: e.target.value }))}
                rows={4}
                style={{ width: '100%', padding: '0.9rem 1rem', background: 'rgba(15,23,42,0.9)', color: '#e8d5a0', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '6px', fontSize: '0.85rem', lineHeight: 1.7, resize: 'vertical', fontStyle: 'italic', fontFamily: 'Georgia, serif', boxSizing: 'border-box' }}
              />
            </div>

            {/* Clause IV: Signature Block */}
            <div style={{ background: '#080d12', border: '1px solid rgba(201,168,76,0.3)', borderTop: '1px dashed rgba(201,168,76,0.12)', padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.9rem' }}>
                <span style={{ fontSize: '0.58rem', fontWeight: '900', color: '#c9a84c', letterSpacing: '2px' }}>IV.</span>
                <span style={{ fontSize: '0.62rem', fontWeight: '800', color: '#c9a84c', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Signature Block, Digital Ink Attachment</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
                {/* Signature upload */}
                <div>
                  <label style={{ display: 'block', fontSize: '0.6rem', fontWeight: '700', color: '#c9a84c', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    Wet-Ink / Digital Signature Image
                  </label>
                  <div style={{ border: '1px dashed rgba(201,168,76,0.35)', borderRadius: '6px', padding: '0.75rem', background: 'rgba(201,168,76,0.03)', minHeight: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    {affidavit.signatureAttachment?.dataUrl ? (
                      <div style={{ textAlign: 'center' }}>
                        <img src={affidavit.signatureAttachment.dataUrl} alt="Signature"
                          style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '4px', background: 'rgba(255,255,255,0.04)' }} />
                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.4rem' }}>{affidavit.signatureAttachment.name}</div>
                        <button type="button" onClick={() => setAffidavit(prev => ({ ...prev, signatureAttachment: null }))}
                          style={{ marginTop: '0.4rem', background: 'none', border: '1px solid #334155', color: '#64748b', borderRadius: '4px', padding: '0.2rem 0.6rem', cursor: 'pointer', fontSize: '0.68rem' }}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <span style={{ fontSize: '1.5rem', opacity: 0.4 }}>&#9998;</span>
                        <label style={{ cursor: 'pointer', fontSize: '0.7rem', color: '#c9a84c', fontWeight: '600' }}>
                          Attach Signature (PNG / JPEG / WEBP, max 1 MB)
                          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleSignatureAttachment} style={{ display: 'none' }} />
                        </label>
                        <div style={{ fontSize: '0.62rem', color: '#334155', textAlign: 'center' }}>
                          Upload an authorized digital or wet-ink signature image
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Signatory identity panel */}
                <div style={{ fontSize: '0.72rem', lineHeight: 1.8 }}>
                  <div style={{ fontSize: '0.6rem', fontWeight: '700', color: '#c9a84c', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    Signatory Identity on Record
                  </div>
                  {[
                    { k: 'Entity', v: affidavit.executingEntity },
                    { k: 'Role', v: affidavit.signatoryRole },
                    { k: 'Virtual ID Credential', v: affidavit.signatoryOor ? affidavit.signatoryOor.slice(0, 36) + '...' : '' },
                    { k: 'LEI', v: affidavit.lei },
                  ].map(({ k, v }) => (
                    <div key={k} style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid rgba(201,168,76,0.08)', paddingBottom: '0.2rem', marginBottom: '0.2rem' }}>
                      <span style={{ color: '#475569', minWidth: '110px', flexShrink: 0 }}>{k}</span>
                      <span style={{ color: '#e8d5a0', fontFamily: 'monospace', fontSize: '0.68rem', wordBreak: 'break-all' }}>{v || <em style={{ color: '#334155' }}>not set</em>}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Jurat / Notarization Block */}
            <div style={{ background: 'linear-gradient(180deg, #080d12 0%, #060b0f 100%)', border: '1px solid rgba(201,168,76,0.3)', borderTop: '1px dashed rgba(201,168,76,0.12)', padding: '1.1rem 1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '50%', flexShrink: 0, border: '2px solid rgba(201,168,76,0.4)', background: 'rgba(201,168,76,0.05)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '1.2rem' }}>&#9878;</span>
                  <span style={{ fontSize: '0.42rem', color: 'rgba(201,168,76,0.7)', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', marginTop: '2px' }}>NOTARY</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.62rem', fontWeight: '800', color: '#c9a84c', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
                    Jurat, Digital Notarization Record
                  </div>
                  <p style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.7, margin: 0, fontStyle: 'italic' }}>
                    SUBSCRIBED AND AFFIRMED before the K-R-O Private Market Authority of Record on{' '}
                    <strong style={{ color: '#e8d5a0' }}>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
                    This attestation shall be anchored to the encrypted witness network (modeled on KERI best practices)
                    Authentic Chained Data Container. Nonce: <code style={{ color: '#c9a84c', fontFamily: 'monospace' }}>{affidavit.nonce}</code>.
                    Upon submission, a cryptographic hash of this instrument is sealed to the Provenance Blockchain
                    and Canton Network as an immutable authority of record.
                  </p>
                </div>
              </div>
            </div>

            {/* OPT-IN CONSENT + Submit */}
            <div style={{ background: 'rgba(201,168,76,0.04)', border: '2px solid rgba(201,168,76,0.5)', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '1.25rem 1.5rem' }}>
              <div style={{ fontSize: '0.62rem', fontWeight: '800', color: '#c9a84c', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                OPT-IN CONSENT, Authority of Record Engagement
              </div>
              {[
                { bold: 'I HEREBY CERTIFY', text: ', under penalty of providing false information in a private market instrument, that all information contained in this affidavit is true, accurate, and complete to the best of my knowledge, and that I am duly authorized to execute this attestation on behalf of the named legal entity.' },
                { bold: 'I UNDERSTAND AND AGREE', text: ' that this tokenized attestation, upon submission, constitutes a binding authority of record for the K-R-O Private Market and will be anchored on-chain using an encrypted credential structure modeled on vLEI/KERI best practices with ISO 17442 LEI binding. This credential cannot be unilaterally revoked once the escrow order is executed.' },
                { bold: 'I ACKNOWLEDGE', text: ' that this affidavit engages my encrypted organizational role credential modeled on vLEI best practices, and that the event log entry generated by this submission is subject to multi-signature witness verification and is permanently discoverable through KRO\'s settlement audit infrastructure.' },
              ].map(({ bold, text }, idx) => (
                <label key={idx} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: idx < 2 ? '0.75rem' : '1.25rem', cursor: 'pointer' }}>
                  <input type="checkbox" defaultChecked style={{ marginTop: '3px', accentColor: '#c9a84c', width: '14px', height: '14px', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.6 }}>
                    <strong style={{ color: '#e8d5a0' }}>{bold}</strong>{text}
                  </span>
                </label>
              ))}

              {/* Submit / push row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', paddingTop: '0.5rem', borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                <button onClick={pushAffidavitToKRO} disabled={affidavitSaving}
                  style={{ padding: '0.65rem 1.5rem', background: affidavitSaving ? 'rgba(201,168,76,0.3)' : 'linear-gradient(135deg, #b7860c, #c9a84c)', color: '#0c0f14', fontWeight: '800', fontSize: '0.82rem', letterSpacing: '0.5px', border: 'none', borderRadius: '6px', cursor: affidavitSaving ? 'not-allowed' : 'pointer' }}>
                  {affidavitSaving ? 'Saving to Database...' : 'Submit & Save to Database — Push to KRO Execution Gateway'}
                </button>
                {affidavitStatus && (
                  <span style={{ fontSize: '0.78rem', color: affidavitStatus.startsWith('Error') || affidavitStatus.startsWith('Network') ? '#ef4444' : '#10b981' }}>
                    {affidavitStatus}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* AttestationRecords table */}
            <div style={{ background: '#1e293b', borderRadius: '8px', padding: '1rem 1.5rem' }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div style={{ fontSize: '0.72rem', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
                  <i className="bi bi-table me-1"></i>AttestationRecords
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', fontSize: '0.6rem' }}>
                    {allRecords.length} record{allRecords.length !== 1 ? 's' : ''}
                  </span>
                  <button onClick={fetchAllRecords} style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem', background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid #334155', borderRadius: '4px', cursor: 'pointer' }}>
                    <i className="bi bi-arrow-repeat me-1"></i>Refresh
                  </button>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-dark table-sm align-middle mb-0" style={{ fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      <th style={{ color: '#94a3b8' }}>ID</th>
                      <th style={{ color: '#94a3b8' }}>Source</th>
                      <th style={{ color: '#94a3b8' }}>Status</th>
                      <th style={{ color: '#94a3b8' }}>LEI</th>
                      <th style={{ color: '#94a3b8' }}>Signatory</th>
                      <th style={{ color: '#94a3b8' }}>Settlement Type</th>
                      <th style={{ color: '#94a3b8' }}>Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRecords.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-3" style={{ color: '#475569' }}>
                          <i className="bi bi-inbox me-1"></i>
                          No attestation records found. Submit an affidavit to create the first record.
                        </td>
                      </tr>
                    ) : allRecords.map(rec => (
                      <tr key={rec.id} style={{ borderBottom: '1px solid #1e293b', cursor: 'pointer' }}
                        onClick={() => {
                          setAffidavit({
                            nonce: rec.nonce ?? 1,
                            lei: rec.lei ?? '', organizationVleiRoot: rec.organizationVleiRoot ?? '',
                            signatoryOor: rec.signatoryOor ?? '', signatoryRole: rec.signatoryRole ?? '',
                            executingEntity: rec.executingEntity ?? '', executingWallet: rec.executingWallet ?? '',
                            fundingSource: rec.fundingSource ?? '', settlementType: rec.settlementType ?? '',
                            priority: rec.priority ?? '', destination: rec.destination ?? '',
                            attestation: rec.attestation ?? '', signatureAttachment: rec.signatureAttachment ?? null,
                          });
                          setAffidavitRecordId(rec.id);
                          setAffidavitLastSync(rec.updatedAt);
                        }}>
                        <td><code style={{ color: '#818cf8', fontSize: '0.7rem' }}>{rec.id.slice(0, 8)}...</code></td>
                        <td><span className={`badge ${rec.source === 'TTD_STUDIO' ? 'bg-warning text-dark' : 'bg-info'}`} style={{ fontSize: '0.6rem' }}>{rec.source}</span></td>
                        <td><span className={`badge ${rec.status === 'ACTIVE' ? 'bg-success' : rec.status === 'SUBMITTED' ? 'bg-primary' : 'bg-secondary'}`} style={{ fontSize: '0.6rem' }}>{rec.status}</span></td>
                        <td style={{ color: '#e2e8f0' }}>{rec.lei || '—'}</td>
                        <td style={{ color: '#94a3b8' }}>{rec.signatoryRole || rec.signatoryOor || '—'}</td>
                        <td style={{ color: '#94a3b8' }}>{rec.settlementType || '—'}</td>
                        <td style={{ color: '#64748b' }}>{rec.updatedAt ? new Date(rec.updatedAt).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
        </div>
      )}

      {/* ── Sandbox E2E Test View ── */}
      {activeView === 'sandbox-test' && (
        <div className="card border-0 shadow-sm mb-4" style={{ background: '#0f172a', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ background: 'linear-gradient(135deg, #78350f, #451a03)', borderBottom: '1px solid rgba(245,158,11,0.15)' }}>
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-play-circle-fill text-warning fs-4"></i>
              <div>
                <h5 className="mb-0 fw-bold text-white">Sandbox E2E Settlement Test</h5>
                <small className="text-white-50">Full DvP pipeline: Affidavit → Provenance → Canton → BitGo → CIP-56 Cash Leg → Mercury → Registry Receipt</small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {sandboxReceipt && (
                <span className="badge bg-success px-3 py-2">
                  <i className="bi bi-check-circle-fill me-1"></i>TEST PASSED
                </span>
              )}
              {sandboxRunning && (
                <span className="badge bg-warning text-dark px-3 py-2">
                  <i className="bi bi-hourglass-split me-1 efcp-spin"></i>RUNNING — Step {sandboxStep + 1}/10
                </span>
              )}
              <button
                className={`btn btn-sm fw-bold ${sandboxRunning ? 'btn-outline-secondary' : 'btn-warning'}`}
                onClick={runSandboxTest}
                disabled={sandboxRunning}
              >
                <i className={`bi ${sandboxRunning ? 'bi-hourglass-split efcp-spin' : 'bi-play-fill'} me-1`}></i>
                {sandboxRunning ? 'Test Running...' : sandboxReceipt ? 'Run Again' : 'Start E2E Test'}
              </button>
            </div>
          </div>

          {/* Step progress bar */}
          {(sandboxRunning || sandboxReceipt) && (
            <div className="px-3 py-2" style={{ background: 'rgba(245,158,11,0.04)', borderBottom: '1px solid rgba(245,158,11,0.1)' }}>
              <div className="d-flex gap-1 align-items-center mb-1">
                {['Affidavit', 'Provenance', 'RFQ', 'CIP-0056', 'BitGo', 'Clearing', 'GlobalSync', 'Canton DvP', 'Mercury', 'Receipt'].map((name, i) => (
                  <div key={name} className="d-flex align-items-center flex-grow-1">
                    <div className="d-flex flex-column align-items-center flex-grow-1">
                      <span style={{
                        width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.55rem', fontWeight: 800,
                        background: sandboxStep > i ? '#10b981' : sandboxStep === i ? '#f59e0b' : '#1e293b',
                        color: sandboxStep >= i ? '#fff' : '#475569',
                        border: `2px solid ${sandboxStep > i ? '#10b981' : sandboxStep === i ? '#f59e0b' : '#334155'}`,
                        boxShadow: sandboxStep === i ? '0 0 8px rgba(245,158,11,0.5)' : 'none',
                      }}>
                        {sandboxStep > i ? <i className="bi bi-check"></i> : i + 1}
                      </span>
                      <span style={{ fontSize: '0.5rem', color: sandboxStep >= i ? '#e2e8f0' : '#475569', marginTop: 2, fontWeight: 600, whiteSpace: 'nowrap' }}>{name}</span>
                    </div>
                    {i < 10 && (
                      <div style={{ height: 2, flex: 1, minWidth: 6, background: sandboxStep > i ? '#10b981' : '#334155', marginBottom: 14 }}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card-body p-3">
            {/* Pre-test info */}
            {/* Network readiness indicator */}
            <div className="d-flex align-items-center gap-3 p-3 rounded-3 mb-3 flex-wrap"
              style={{ background: `${networkBadgeColor}0a`, border: `1px solid ${networkBadgeColor}2a` }}>
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <i className="bi bi-hdd-network-fill" style={{ color: networkBadgeColor, fontSize: '1rem' }}></i>
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: '800', color: networkBadgeColor, letterSpacing: '0.8px' }}>
                    {networkPillLabel} · Canton kro-settlement v{cantonNetworkStatus.packageVersion || '0.1.3'}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: '1px' }}>
                    {isMainnetLive
                      ? 'MainNet deployed — counterparty onboarding required before live execution. Smoke test always available.'
                      : isTestnetActive
                      ? 'TestNet active — integration testing phase. Sandbox simulates full flow against TestNet contracts.'
                      : 'DevNet active — development environment live. Smoke test validates full 11-stage pipeline.'
                    }
                  </div>
                </div>
              </div>
              <span className="badge fw-bold" style={{
                background: isMainnetLive ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.12)',
                color: isMainnetLive ? '#10b981' : '#f59e0b',
                fontSize: '0.62rem',
              }}>
                {isMainnetLive ? 'SMOKE TEST MODE' : 'SANDBOX MODE'}
              </span>
            </div>

            {sandboxStep === -1 && !sandboxReceipt && (
              <div className="p-4 rounded-3 text-center" style={{ background: 'rgba(245,158,11,0.04)', border: '1px dashed rgba(245,158,11,0.3)' }}>
                <i className="bi bi-diagram-3-fill text-warning" style={{ fontSize: '3rem', opacity: 0.6 }}></i>
                <h6 className="text-white fw-bold mt-3">Ready to Run End-to-End Settlement Test</h6>
                <p className="text-white-50 mb-3" style={{ fontSize: '0.82rem', maxWidth: 600, margin: '0 auto' }}>
                  This sandbox test simulates the full DvP settlement pipeline across all 10 stages.
                  It generates a unique <strong className="text-warning">Public Root Registry Key</strong> and
                  auto-numbered <strong className="text-warning">Registry Receipt</strong> as proof of execution.
                  Each step produces real timestamps and cryptographic identifiers that can be cross-referenced.
                </p>
                <div className="d-flex justify-content-center gap-3 flex-wrap mb-3">
                  {['Attestation of Affidavit', 'Provenance Marker', 'Canton DvP', 'BitGo MPC', 'CIP-56 Cash Leg', 'Mercury FedWire', 'Registry Receipt'].map(s => (
                    <span key={s} className="badge" style={{ background: 'rgba(245,158,11,0.08)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.2)', fontSize: '0.65rem' }}>{s}</span>
                  ))}
                </div>
                <button className="btn btn-warning btn-lg fw-bold" onClick={runSandboxTest}>
                  <i className="bi bi-play-fill me-2"></i>Start E2E Settlement Test
                </button>
              </div>
            )}

            {/* Live execution log */}
            {sandboxLog.length > 0 && (
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: '#64748b' }}>
                    <i className="bi bi-terminal-fill me-1"></i>Execution Log
                  </span>
                  <span className="badge bg-dark" style={{ fontSize: '0.62rem' }}>{sandboxLog.length} events</span>
                </div>
                <div className="rounded-3 p-2" style={{ background: '#020617', border: '1px solid #1e293b', maxHeight: 350, overflowY: 'auto', fontFamily: 'monospace' }}>
                  {sandboxLog.map((entry, idx) => (
                    <div key={idx} style={{
                      fontSize: '0.72rem', lineHeight: 1.6, padding: '1px 4px',
                      color: entry.type === 'success' ? '#10b981' : entry.type === 'header' ? '#f59e0b' : entry.type === 'key' ? '#a78bfa' : entry.type === 'complete' ? '#38bdf8' : '#94a3b8',
                      fontWeight: entry.type === 'header' || entry.type === 'key' || entry.type === 'complete' ? 700 : 400,
                      background: entry.type === 'complete' ? 'rgba(56,189,248,0.06)' : entry.type === 'key' ? 'rgba(167,139,250,0.06)' : 'transparent',
                      borderRadius: 4,
                    }}>
                      <span style={{ color: '#475569', fontSize: '0.62rem', marginRight: 8 }}>{new Date(entry.ts).toLocaleTimeString()}</span>
                      {entry.msg}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Registry Receipt */}
            {sandboxReceipt && (
              <div className="rounded-3 overflow-hidden" style={{ border: '2px solid #10b981' }}>
                <div className="p-3" style={{ background: 'linear-gradient(135deg, #064e3b, #0f172a)' }}>
                  <div className="text-center mb-3">
                    <div style={{ fontSize: '0.55rem', fontWeight: 800, letterSpacing: '3px', color: '#6ee7b7', textTransform: 'uppercase' }}>Sandbox Settlement Test</div>
                    <div className="fw-bold text-white" style={{ fontSize: '1.2rem', letterSpacing: '1px' }}>REGISTRY RECEIPT</div>
                    <div style={{ fontSize: '0.65rem', color: '#6ee7b7', fontStyle: 'italic' }}>End-to-End DvP Pipeline — Proof of Execution</div>
                    <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(16,185,129,0.4), transparent)', margin: '0.75rem auto', maxWidth: 400 }}></div>
                  </div>

                  {/* Root Registry Key — hero display */}
                  <div className="text-center mb-3 p-3 rounded-3" style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.3)' }}>
                    <div style={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: '2px', color: '#a78bfa', textTransform: 'uppercase', marginBottom: 4 }}>Public Root Registry Key</div>
                    <div className="fw-bold" style={{ fontSize: '1.4rem', color: '#e9d5ff', fontFamily: 'monospace', letterSpacing: '2px', wordBreak: 'break-all' }}>
                      {sandboxReceipt.rootRegistryKey}
                    </div>
                    <div style={{ fontSize: '0.62rem', color: '#64748b', marginTop: 4 }}>Registry Sequence #{sandboxReceipt.registrySeq} · Auto-generated · Unique per test run</div>
                  </div>

                  {/* Cross-rail identifiers table */}
                  <div className="table-responsive mb-3">
                    <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.75rem' }}>
                      <thead><tr style={{ borderBottom: '1px solid #334155' }}><th style={{ color: '#6ee7b7' }}>Identifier</th><th style={{ color: '#6ee7b7' }}>Value</th><th style={{ color: '#6ee7b7' }}>Provider</th></tr></thead>
                      <tbody>
                        {[
                          { id: 'Test ID', val: sandboxReceipt.testId, prov: 'KRO Studio' },
                          { id: 'Root Registry Key', val: sandboxReceipt.rootRegistryKey, prov: 'Auto-generated' },
                          { id: 'UETR', val: sandboxReceipt.uetr, prov: 'Canton · Mercury memo' },
                          { id: 'Canton Contract ID', val: sandboxReceipt.cantonContractId, prov: 'Canton Network' },
                          { id: 'Provenance TX Hash', val: `${sandboxReceipt.provenanceTxHash.slice(0,16)}...${sandboxReceipt.provenanceTxHash.slice(-8)}`, prov: 'Provenance · pio-mainnet-1' },
                          { id: 'Provenance Block', val: sandboxReceipt.blockHeight, prov: 'pio-mainnet-1' },
                          { id: 'BitGo TX Hash', val: `${sandboxReceipt.bitgoTxHash.slice(0,16)}...${sandboxReceipt.bitgoTxHash.slice(-8)}`, prov: 'BitGo Enterprise MPC' },
                          { id: 'FedWire Ref', val: sandboxReceipt.fedRef, prov: 'Federal Reserve' },
                          { id: 'Completed', val: new Date(sandboxReceipt.completedAt).toLocaleString(), prov: 'Timestamp' },
                        ].map(row => (
                          <tr key={row.id} style={{ borderBottom: '1px solid #1e293b' }}>
                            <td className="fw-bold" style={{ color: '#e2e8f0' }}>{row.id}</td>
                            <td><code style={{ color: '#818cf8', fontSize: '0.68rem', wordBreak: 'break-all' }}>{row.val}</code></td>
                            <td style={{ color: '#64748b', fontSize: '0.68rem' }}>{row.prov}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pipeline steps summary */}
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    {sandboxReceipt.steps.map((s, i) => (
                      <span key={i} className="badge bg-success d-flex align-items-center gap-1" style={{ fontSize: '0.62rem' }}>
                        <i className="bi bi-check-circle-fill"></i>Step {i + 1}: {s.split('—')[0].trim()}
                      </span>
                    ))}
                  </div>

                  {/* Proof notice */}
                  <div className="p-2 rounded" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', fontSize: '0.72rem', color: '#94a3b8', lineHeight: 1.6 }}>
                    <i className="bi bi-shield-fill-check text-success me-1"></i>
                    <strong className="text-white">Sandbox Proof of Execution:</strong> This receipt confirms successful completion of all settlement pipeline stages.
                    The Root Registry Key <code style={{ color: '#a78bfa' }}>{sandboxReceipt.rootRegistryKey}</code> is unique to this test run and serves as the public anchor.
                    Cross-reference identifiers above with Provenance (ZoneScan), Canton (Ledger API), BitGo (Enterprise API), and Mercury (transaction search by UETR <code style={{ color: '#818cf8' }}>{sandboxReceipt.uetr?.slice(0,13)}</code>).
                    Timestamp <code style={{ color: '#6ee7b7' }}>{sandboxReceipt.completedAt}</code> is system-generated and cannot be backdated.
                  </div>

                  {/* Copy / Export actions */}
                  <div className="d-flex gap-2 mt-3 flex-wrap">
                    <button className="btn btn-sm btn-outline-success fw-bold" onClick={() => {
                      const text = `SANDBOX E2E SETTLEMENT TEST — REGISTRY RECEIPT\n${'='.repeat(55)}\nRoot Registry Key: ${sandboxReceipt.rootRegistryKey}\nRegistry Sequence: #${sandboxReceipt.registrySeq}\nTest ID: ${sandboxReceipt.testId}\nCompleted: ${sandboxReceipt.completedAt}\n\nCROSS-RAIL IDENTIFIERS\n${'-'.repeat(40)}\nUETR: ${sandboxReceipt.uetr}\nCanton Contract: ${sandboxReceipt.cantonContractId}\nProvenance TX: ${sandboxReceipt.provenanceTxHash}\nProvenance Block: ${sandboxReceipt.blockHeight}\nBitGo TX: ${sandboxReceipt.bitgoTxHash}\nFedWire Ref: ${sandboxReceipt.fedRef}\n\nPIPELINE STEPS (10/10 PASSED)\n${'-'.repeat(40)}\n${sandboxReceipt.steps.map((s,i) => `${i+1}. ${s} ✓`).join('\n')}\n\nThis receipt was generated by Trust Tech Studio Sandbox.\nRoot Registry Key is unique per test run and serves as the public proof anchor.`;
                      navigator.clipboard.writeText(text);
                    }}>
                      <i className="bi bi-clipboard me-1"></i>Copy Receipt to Clipboard
                    </button>
                    <button className="btn btn-sm btn-outline-info fw-bold" onClick={() => {
                      const text = `SANDBOX E2E SETTLEMENT TEST — REGISTRY RECEIPT\n${'='.repeat(55)}\nRoot Registry Key: ${sandboxReceipt.rootRegistryKey}\nRegistry Sequence: #${sandboxReceipt.registrySeq}\nTest ID: ${sandboxReceipt.testId}\nCompleted: ${sandboxReceipt.completedAt}\n\nCROSS-RAIL IDENTIFIERS\n${'-'.repeat(40)}\nUETR: ${sandboxReceipt.uetr}\nCanton Contract: ${sandboxReceipt.cantonContractId}\nProvenance TX: ${sandboxReceipt.provenanceTxHash}\nProvenance Block: ${sandboxReceipt.blockHeight}\nBitGo TX: ${sandboxReceipt.bitgoTxHash}\nFedWire Ref: ${sandboxReceipt.fedRef}\n\nPIPELINE STEPS (10/10 PASSED)\n${'-'.repeat(40)}\n${sandboxReceipt.steps.map((s,i) => `${i+1}. ${s}`).join('\n')}`;
                      const blob = new Blob([text], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = `registry-receipt-${sandboxReceipt.registrySeq}.txt`; a.click();
                      URL.revokeObjectURL(url);
                    }}>
                      <i className="bi bi-download me-1"></i>Download Receipt (.txt)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Execution Verification (consolidated view) ── */}
      {activeView === 'verification' && (
        <>
      {/* ── KRO Exchange Registry — TTD Portal Sync ── */}
      <KROExchangeRegistry />

      {/* ── Execution Flow Confirmation Panel ── */}
      <ExecutionFlowPanel />

      {/* ── Third-Party Independent Verification — Proof of Execution ── */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b', border: '1px solid rgba(99,102,241,0.15)' }}>
        <div className="card-header border-0" style={{ background: 'linear-gradient(135deg, #312e81, #1e1b4b)', borderBottom: '1px solid rgba(99,102,241,0.15)' }}>
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-shield-fill-check text-info fs-4"></i>
            <div>
              <h5 className="mb-0 fw-bold text-white">Third-Party Proof of Execution</h5>
              <small className="text-white-50">How to independently verify each provider executed and received commands — no KRO infrastructure involved</small>
            </div>
          </div>
        </div>
        <div className="card-body p-3">
          <div className="d-flex flex-column gap-3">

            {/* 1. Provenance */}
            <div className="p-3 rounded-3" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-link-45deg" style={{ color: '#10b981', fontSize: '1.1rem' }}></i>
                <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>1. Provenance Blockchain — On-Chain Proof</span>
                <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>VERIFIABLE NOW</span>
              </div>
              <p className="mb-2" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>
                ZoneScan is an independent block explorer operated by Figure Technologies (not KRO). Click any marker denom below to see live supply, holder count, and every TX hash on-chain. Each MINT, TRANSFER, WITHDRAW, and BURN event is permanently recorded on <code className="text-light">pio-mainnet-1</code>.
              </p>
              <div className="mb-2" style={{ fontSize: '0.72rem', color: '#64748b' }}>
                <strong className="text-white d-block mb-1">How to verify:</strong>
                Open any link below → check "Supply" matches our reported units → click "Transactions" tab → each TX hash is independently timestamped and signed by the Provenance validator set.
              </div>
              <div className="d-flex flex-wrap gap-2">
                <a href="https://zonescan.io/provenance/assets/kro.as.accountability.v2" target="_blank" rel="noopener noreferrer"
                  className="btn btn-sm" style={{ background: '#10b98115', border: '1px solid #10b98140', color: '#10b981', fontSize: '0.72rem' }}>
                  <i className="bi bi-box-arrow-up-right me-1"></i>kro.as.accountability.v2 (182 units)
                </a>
                <a href="https://zonescan.io/provenance/assets/kro.gipr.genesis.v2" target="_blank" rel="noopener noreferrer"
                  className="btn btn-sm" style={{ background: '#f59e0b15', border: '1px solid #f59e0b40', color: '#f59e0b', fontSize: '0.72rem' }}>
                  <i className="bi bi-box-arrow-up-right me-1"></i>kro.gipr.genesis.v2 (60 units)
                </a>
                <a href="https://zonescan.io/provenance/assets/kro.sc.stewardship.v2" target="_blank" rel="noopener noreferrer"
                  className="btn btn-sm" style={{ background: '#818cf815', border: '1px solid #818cf840', color: '#818cf8', fontSize: '0.72rem' }}>
                  <i className="bi bi-box-arrow-up-right me-1"></i>kro.sc.stewardship.v2 (12 units)
                </a>
              </div>
            </div>

            {/* 2. Canton */}
            <div className="p-3 rounded-3" style={{ background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.2)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-hdd-network-fill" style={{ color: '#38bdf8', fontSize: '1.1rem' }}></i>
                <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>2. Canton Network — Daml Ledger Verification</span>
                <span className="badge bg-info" style={{ fontSize: '0.6rem' }}>PARTICIPANT API</span>
              </div>
              <p className="mb-2" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>
                Canton is a privacy-enabled network — contracts are not publicly browsable like Provenance. Verification requires participant-level API access.
              </p>
              <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.7 }}>
                <strong className="text-white d-block mb-1">How to verify:</strong>
                <ol className="mb-0 ps-3" style={{ color: '#94a3b8' }}>
                  <li>Request read-only Ledger API access from KRO's Canton participant node (<code className="text-light">KRO-Mainnet-1</code>) operated by Infra Singularity.</li>
                  <li>Query: <code className="text-light">{'grpcurl -d \'{ "filter": { "templateIds": ["DrawWorkflow", "SettlementWorkflow"] } }\'  KRO-Mainnet-1:443 com.daml.ledger.api.v1.TransactionService/GetTransactions'}</code></li>
                  <li>The response includes contract IDs, create/exercise events, and signatories — matching the contract ID <code className="text-light">DrawWorkflow#12a4</code> shown in this panel.</li>
                  <li>Contact: <code className="text-light">ops@infrasingularity.com</code> or <code className="text-light">isadmin@krotrust.org</code> for audit access credentials.</li>
                </ol>
              </div>
            </div>

            {/* 3. BitGo */}
            <div className="p-3 rounded-3" style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.2)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-shield-lock-fill" style={{ color: '#f59e0b', fontSize: '1.1rem' }}></i>
                <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>3. BitGo Enterprise — MPC Custody Verification</span>
                <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>ENTERPRISE AUTH</span>
              </div>
              <p className="mb-2" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>
                BitGo Enterprise is a SOC 2 Type II audited custodian. Wallet balances and MPC transaction history are not public — verification requires enterprise API access or an audit request.
              </p>
              <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.7 }}>
                <strong className="text-white d-block mb-1">How to verify:</strong>
                <ol className="mb-0 ps-3" style={{ color: '#94a3b8' }}>
                  <li>BitGo API: <code className="text-light">GET /api/v2/thash/wallet/{'{'}walletId{'}'}/transfer</code> — returns every transfer with TX hash, timestamp, and MPC approval chain.</li>
                  <li>HASH wallet (<code className="text-light">pb1mktt...5mp6</code>): Query Provenance chain for this address to see all marker holdings independently on ZoneScan.</li>
                  <li>ETH wallet (<code className="text-light">0x6855...5ba6</code>): Verify USDT escrow balance on <a href="https://etherscan.io" target="_blank" rel="noopener noreferrer" style={{ color: '#f59e0b' }}>Etherscan</a> — search the address directly.</li>
                  <li>For full audit trail, request BitGo Enterprise audit report via <code className="text-light">enterprise-support@bitgo.com</code> referencing KRO Brokers SPC enterprise account.</li>
                </ol>
              </div>
            </div>

            {/* 4. On-Ledger CIP-56 Settlement Records */}
            <div className="p-3 rounded-3" style={{ background: 'rgba(129,140,248,0.04)', border: '1px solid rgba(129,140,248,0.2)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-file-earmark-code-fill" style={{ color: '#818cf8', fontSize: '1.1rem' }}></i>
                <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>4. On-Ledger CIP-56 Settlement Records — Canton Ledger API</span>
                <span className="badge bg-primary" style={{ fontSize: '0.6rem' }}>ON-LEDGER</span>
              </div>
              <p className="mb-2" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>
                The cash leg settles on-chain as a CIP-56 token via atomic DvP — there is no off-ledger message chain. Every Allocation, Allocation_ExecuteTransfer, and CloseOutReceipt is a Canton contract recorded on the ledger and queryable through the Daml Ledger API.
              </p>
              <div className="mb-2" style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.7 }}>
                <strong className="text-white d-block mb-1">How to verify:</strong>
                <ol className="mb-0 ps-3" style={{ color: '#94a3b8' }}>
                  <li>Query the Canton Ledger API on <code className="text-light">KRO-Mainnet-1</code> → <code className="text-light">GetActiveContracts</code> / <code className="text-light">GetTransactions</code> for the settlement window.</li>
                  <li>Match the <code className="text-light">CloseOutReceipt</code> contract ID and <code className="text-light">UETR</code> to the cross-rail references above.</li>
                  <li>Inspect the <code className="text-light">Allocation → Allocation_ExecuteTransfer → CloseOutReceipt</code> lifecycle — both legs settle atomically or roll back.</li>
                  <li>Canton consensus timestamps and block height are deterministic and cannot be backdated.</li>
                </ol>
              </div>
            </div>

            {/* 5. Mercury / FedWire */}
            <div className="p-3 rounded-3" style={{ background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.2)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-building-fill" style={{ color: '#a78bfa', fontSize: '1.1rem' }}></i>
                <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>5. Mercury → Column N.A. → FedWire — Banking Rail Proof</span>
                <span className="badge" style={{ background: '#a78bfa22', color: '#a78bfa', fontSize: '0.6rem' }}>BANKING RAIL</span>
              </div>
              <p className="mb-2" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>
                Mercury is a banking platform built on Column N.A. (FDIC-insured). FedWire transactions are processed by the Federal Reserve and are independently verifiable through bank statements and Fed reference numbers.
              </p>
              <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.7 }}>
                <strong className="text-white d-block mb-1">How to verify wire receipt at Mercury:</strong>
                <ol className="mb-0 ps-3" style={{ color: '#94a3b8' }}>
                  <li>Mercury API: <code className="text-light">GET /api/v1/transactions?search=&lt;UETR&gt;</code> — searches by the UETR embedded in the wire memo.</li>
                  <li>The wire arrives via Column N.A. routing <code className="text-light">121145433</code> with SWIFT <code className="text-light">CLUMUS33XXX</code>.</li>
                  <li>Mercury transaction detail shows: Fed reference number, timestamp, originator, amount, and memo (containing the UETR).</li>
                  <li>For third-party audit: request Mercury bank statement for the settlement date. Column N.A. is a nationally-chartered bank — statements are legally auditable records.</li>
                  <li>FedWire confirmation: the Fed reference number on the Mercury transaction is independently assigned by the Federal Reserve Wire Network — not by KRO or Mercury.</li>
                </ol>
              </div>
              <div className="mt-2 p-2 rounded" style={{ background: 'rgba(167,139,250,0.06)', fontSize: '0.7rem', color: '#a78bfa' }}>
                <i className="bi bi-exclamation-circle me-1"></i>
                <strong>Note:</strong> Mercury dashboard access requires authenticated login to the KRO Brokers SPC business account. For independent audit, request a signed bank statement from Mercury support referencing the FedWire trace number.
              </div>
            </div>

            {/* 6. Webhook / Azure Event Logs */}
            <div className="p-3 rounded-3" style={{ background: 'rgba(244,114,182,0.04)', border: '1px solid rgba(244,114,182,0.2)' }}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <i className="bi bi-broadcast" style={{ color: '#f472b6', fontSize: '1.1rem' }}></i>
                <span className="fw-bold text-white" style={{ fontSize: '0.88rem' }}>6. Webhook Event Logs — Provenance → Azure</span>
                <span className="badge" style={{ background: '#f472b622', color: '#f472b6', fontSize: '0.6rem' }}>EVENT STREAM</span>
              </div>
              <p className="mb-2" style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.6 }}>
                Provenance Blockchain emits webhook events for every marker lifecycle operation. These are received by our Azure Container App and logged in Azure Monitor.
              </p>
              <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.7 }}>
                <strong className="text-white d-block mb-1">How to verify:</strong>
                <ol className="mb-0 ps-3" style={{ color: '#94a3b8' }}>
                  <li>Azure Monitor → Container Apps → <code className="text-light">ca-api-prod-wus2</code> → Logs: <code className="text-light">ContainerAppConsoleLogs_CL | where Log_s contains "webhook" | order by TimeGenerated desc</code></li>
                  <li>Each log entry contains the Provenance TX hash, block height, event type (MINT/TRANSFER/WITHDRAW/BURN), and denom.</li>
                  <li>Cross-reference TX hash from the log with ZoneScan: <code className="text-light">https://zonescan.io/provenance/tx/{'{'}<em>txHash</em>{'}'}</code></li>
                </ol>
              </div>
            </div>

          </div>

          {/* Summary: Cross-Rail Verification Matrix */}
          <div className="mt-3 p-3 rounded-3" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)', borderLeft: '3px solid #6366f1' }}>
            <div className="d-flex gap-2 align-items-start">
              <i className="bi bi-table text-primary mt-1 flex-shrink-0"></i>
              <div>
                <strong className="text-white d-block mb-2" style={{ fontSize: '0.82rem' }}>Cross-Rail Verification Matrix</strong>
                <p className="text-white-50 mb-2" style={{ fontSize: '0.72rem' }}>
                  Use these identifiers to trace the same settlement across all independent systems. If any identifier does not match across systems, the settlement chain is broken.
                </p>
                <div className="table-responsive">
                  <table className="table table-dark table-sm mb-0" style={{ fontSize: '0.72rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #334155' }}>
                        <th style={{ color: '#94a3b8' }}>Identifier</th>
                        <th style={{ color: '#94a3b8' }}>Value</th>
                        <th style={{ color: '#94a3b8' }}>Where to Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { id: 'UETR', val: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890', where: 'Canton CloseOutReceipt; Mercury wire memo' },
                        { id: 'CloseOut Receipt CID', val: '00c2db19...8554', where: 'Canton Ledger API → GetActiveContracts response' },
                        { id: 'Canton Contract ID', val: 'Canton::KRO-Mainnet-1::DrawWorkflow#12a4', where: 'Canton Ledger API → GetTransactions response' },
                        { id: 'Provenance TX Hash', val: '7A3F...E91B', where: 'ZoneScan → search TX hash directly' },
                        { id: 'DTIF', val: 'ZX18049M0', where: 'DTIF Registry (dtif.org); Canton contract metadata' },
                        { id: 'BitGo TX', val: 'MPC transfer ID in BitGo audit log', where: 'BitGo Enterprise API → /transfer endpoint' },
                        { id: 'FedWire Ref', val: 'Assigned by Federal Reserve', where: 'Mercury transaction detail → Fed reference number' },
                      ].map(row => (
                        <tr key={row.id} style={{ borderBottom: '1px solid #1e293b' }}>
                          <td className="fw-bold" style={{ color: '#e2e8f0' }}>{row.id}</td>
                          <td><code style={{ color: '#818cf8', fontSize: '0.68rem' }}>{row.val}</code></td>
                          <td style={{ color: '#94a3b8' }}>{row.where}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* ── TTD Portal Data Feed Ingestion (auto-updated) ── */}
      {(activeView === 'verification' || activeView === 'sandbox-test' || activeView === 'pipeline') && (
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#1e293b' }}>
        <div className="card-header border-bottom" style={{ borderColor: '#334155' }}>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center">
              <i className="bi bi-rss-fill text-warning me-2"></i>
              <h6 className="mb-0 fw-bold text-white">TTD Portal — Data Feed Ingestion</h6>
            </div>
            <div className="d-flex align-items-center gap-2">
              {feedStatus.lastSync && (
                <small style={{ color: '#64748b' }}>Last sync: {new Date(feedStatus.lastSync).toLocaleTimeString()}</small>
              )}
              <button className="btn btn-sm btn-outline-success" onClick={syncFeedStatus} style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem' }}>
                <i className="bi bi-arrow-clockwise me-1"></i>Sync Now
              </button>
            </div>
          </div>
        </div>
        <div className="card-body p-3">
          {/* Execution command status strip */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            {EXECUTION_PROVIDERS.map(p => (
              <span key={p.id} className="badge d-flex align-items-center gap-1" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', color: '#6ee7b7', fontSize: '0.68rem' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block', boxShadow: '0 0 4px #10b981' }}></span>
                {p.name.split('—')[0].split('→')[0].trim()}: <strong>{p.badge}</strong>
              </span>
            ))}
          </div>

          <div className="table-responsive">
            <table className="table table-dark table-sm align-middle mb-0" style={{ fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={{ color: '#94a3b8' }}>Event</th>
                  <th style={{ color: '#94a3b8' }}>Details</th>
                  <th style={{ color: '#94a3b8' }}>Amount</th>
                  <th style={{ color: '#94a3b8' }}>Network</th>
                  <th style={{ color: '#94a3b8' }}>Status</th>
                  <th style={{ color: '#94a3b8' }}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {feedStatus.events.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ color: '#475569', textAlign: 'center', padding: '1.5rem 0', fontStyle: 'italic' }}>
                      No events yet — click Sync Now to load live Canton ledger data
                    </td>
                  </tr>
                )}
                {feedStatus.events.map(ev => {
                  const statusColor = {
                    ACTIVE: '#10b981', SETTLED: '#10b981', CONFIRMED: '#10b981', ROLLED: '#818cf8',
                    CREATED: '#3b82f6', ACCEPTED: '#3b82f6', OPEN: '#3b82f6', RELEASED: '#f59e0b',
                    SENT: '#10b981',
                  }[ev.status] || '#64748b';
                  return (
                  <tr key={ev.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td>
                      <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#e2e8f0' }}>{ev.label || ev.type}</div>
                      <div style={{ fontSize: '0.6rem', color: '#475569' }}>{ev.type}</div>
                    </td>
                    <td style={{ color: '#cbd5e1', fontSize: '0.75rem', maxWidth: 220, wordBreak: 'break-all' }}>{ev.denom}</td>
                    <td style={{ color: '#fcd34d', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {ev.amountUsd ? `$${(ev.amountUsd / 1e6).toFixed(2)}M` : '—'}
                    </td>
                    <td>
                      <span className="badge" style={{ background: 'rgba(51,65,85,0.8)', color: '#94a3b8', fontSize: '0.6rem' }}>
                        {ev.provider || 'Canton'}
                      </span>
                      {ev.env && <div style={{ fontSize: '0.58rem', color: '#334155', marginTop: 2 }}>{ev.env}</div>}
                    </td>
                    <td>
                      <span className="badge" style={{ background: `${statusColor}18`, border: `1px solid ${statusColor}44`, color: statusColor, fontSize: '0.65rem' }}>
                        {ev.status}
                      </span>
                    </td>
                    <td style={{ color: '#64748b', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                      {ev.ts ? new Date(ev.ts).toLocaleString() : '—'}
                    </td>
                  </tr>
                  );
                })}
                {webhookEvents.map((ev, idx) => (
                  <tr key={`wh-${idx}`} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td><span className={`badge bg-${ev.event === 'MINT' ? 'success' : ev.event === 'TRANSFER' ? 'primary' : ev.event === 'WITHDRAW' ? 'warning' : 'danger'}`} style={{ fontSize: '0.68rem' }}>WEBHOOK: {ev.event}</span></td>
                    <td><code style={{ color: '#e2e8f0', fontSize: '0.75rem' }}>{ev.denom}</code></td>
                    <td><span className="badge bg-dark" style={{ fontSize: '0.62rem' }}>Provenance</span></td>
                    <td>
                      <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>RECEIVED</span>
                      <a href={`https://zonescan.io/provenance/tx/${ev.txHash}`} target="_blank" rel="noopener noreferrer"
                        className="ms-1" style={{ fontSize: '0.62rem', color: '#818cf8' }}>
                        {ev.txHash} <i className="bi bi-box-arrow-up-right"></i>
                      </a>
                    </td>
                    <td style={{ color: '#64748b' }}>{new Date(ev.ts).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 p-2 rounded" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', fontSize: '0.78rem', color: '#94a3b8' }}>
            <i className="bi bi-info-circle text-success me-1"></i>
            Data feeds from TTD Portal are ingested into the settlement workflow via webhook and REST polling. All marker operations (assign, transfer, withdraw) and execution commands flow through this gateway.
            Execution status automatically updates upon deployment across all five providers: Provenance, Canton, BitGo, Mercury/FedWire, and the on-chain CIP-56 cash leg.
          </div>
        </div>
      </div>
      )}

      {/* ── Settlement messaging portal removed — cash leg settles on-ledger via CIP-56 atomic DvP ── */}
      {/* Trust Signal alignment footer */}
      <div className="card border-0 shadow-sm mt-4" style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.05), rgba(139,92,246,0.05))', border: '1px solid rgba(99,102,241,0.15)' }}>
        <div className="card-body p-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <i className="bi bi-signpost-split-fill text-primary" style={{ fontSize: '1.3rem' }}></i>
            <div>
              <h6 className="mb-0 fw-bold" style={{ color: '#e2e8f0', fontSize: '0.85rem' }}>Settlement ↔ Trust Signal Alignment</h6>
              <small style={{ color: '#94a3b8' }}>
                Inertia (K-R-O) · Acceleration (This OS) · Latency (B2All) — signal metrics feed settlement confidence scoring and marker assignment velocity.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutionGateway;
